(function () {
  'use strict';

  const POPUP_ID = 'MiniCartUpsellPopup';
  const PRODUCT_DRAWER_ID = 'MiniCartUpsellProductDrawer';
  const CART_DRAWER_ID = 'CartDrawer';
  const MCU_SLIDE_MS = 420;
  const getPopup = () => document.getElementById(POPUP_ID);
  const getCartDrawer = () => document.getElementById(CART_DRAWER_ID);
  const getProductDrawer = () => {
    const drawer = getCartDrawer();
    return drawer ? drawer.querySelector('#' + PRODUCT_DRAWER_ID) : document.getElementById(PRODUCT_DRAWER_ID);
  };
  const getProductDrawerHost = () => {
    const drawer = getCartDrawer();
    return drawer ? drawer.querySelector('.cart-drawer__body-main-content') : null;
  };

  let activeProductDrawerProduct = null;
  let activeProductDrawerSelections = {};
  let activeProductDrawerVariantId = null;
  let productDrawerPickerObserver = null;
  let productDrawerUsesPdpPicker = false;

  let pendingMain = null;
  let shouldOpenCartOnClose = false;
  let popupMode = 'pdp';
  const pendingAdds = [];
  let removeQueue = Promise.resolve();
  const removingCartItemKeys = new Set();

  function trackAdd(promise) {
    pendingAdds.push(promise);
    const cleanup = () => {
      const i = pendingAdds.indexOf(promise);
      if (i > -1) pendingAdds.splice(i, 1);
    };
    promise.then(cleanup, cleanup);
    return promise;
  }

  async function flushPendingAdds() {
    if (!pendingAdds.length) return;
    try {
      await Promise.allSettled(pendingAdds.slice());
    } catch (_) {}
  }

  function getSectionId() {
    const drawer = getCartDrawer();
    return drawer ? drawer.getAttribute('data-section-id') || drawer.dataset.sectionId : null;
  }

  /** Theme native cart AJAX uses a section id string, not an array. */
  function getSectionsRequestParam() {
    return getSectionId() || 'cart-drawer';
  }

  function normalizeSectionMap(sections) {
    if (!sections || typeof sections !== 'object') return null;
    const drawer = getCartDrawer();
    const sectionId = getSectionId() || (drawer && drawer.getAttribute('data-section-id')) || 'cart-drawer';
    if (sections[sectionId]) {
      return { [sectionId]: sections[sectionId] };
    }
    const keys = Object.keys(sections);
    if (keys.length === 1) {
      return { [sectionId]: sections[keys[0]] };
    }
    return null;
  }

  function dispatchRefresh(options) {
    const openDrawer = !(options && options.openDrawer === false);
    const detail = { openDrawer };
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail })
    );
    window.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
  }

  function dispatchCartUpdated(data) {
    const detail =
      data && typeof data === 'object'
        ? Object.assign({}, data, { __mcuDrawerSynced: true })
        : { __mcuDrawerSynced: true };
    window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    if (window.Shopify && typeof window.Shopify.onItemAdded === 'function') {
      try {
        window.Shopify.onItemAdded(data);
      } catch (_) {}
    }
  }

  function isCartDrawerMounted() {
    const drawer = getCartDrawer();
    return !!(drawer && drawer.querySelector('[data-sidebar-body]'));
  }

  function isCartDrawerOpen() {
    const drawer = getCartDrawer();
    if (!drawer) return false;
    if (drawer.isOpen === true) return true;
    return drawer.getAttribute('data-aria-expanded') === 'true';
  }

  function openCartDrawer() {
    const drawer = getCartDrawer();
    if (!drawer) return;
    if (typeof drawer.show === 'function') {
      try {
        drawer.show();
        return;
      } catch (_) {}
    }
    if (typeof drawer.open === 'function') {
      try {
        drawer.open();
        return;
      } catch (_) {}
    }
    if (typeof drawer.toggle === 'function' && !isCartDrawerOpen()) {
      try {
        drawer.toggle();
      } catch (_) {}
    }
  }

  function findUpsellMountInSectionRoot(sectionNode) {
    if (!sectionNode) return null;
    let mount = sectionNode.querySelector('[data-cart-drawer-upsell-mount]');
    if (mount) return mount;

    const templates = sectionNode.querySelectorAll(
      'template[data-sidebar-template="CartDrawer"], template[data-sidebar-template], template'
    );
    for (let i = 0; i < templates.length; i++) {
      const tpl = templates[i];
      if (tpl.content) {
        mount = tpl.content.querySelector('[data-cart-drawer-upsell-mount]');
        if (mount) return mount;
      }
    }
    return null;
  }

  function syncDrawerEmptyState() {
    const drawer = getCartDrawer();
    if (!drawer || !isCartDrawerMounted()) return;
    const hasItems = !!drawer.querySelector('cart-item');
    if (drawer.hasAttribute('data-has-warning-placeholder')) {
      drawer.toggleAttribute('is-empty', !hasItems);
    }
  }

  function syncUpsellFromSectionHtml(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionRoot = doc.querySelector('cart-drawer') || doc.body.firstElementChild;
    if (sectionRoot) syncCartDrawerUpsell(sectionRoot);
  }

  function syncUpsellFromSections(sections) {
    const norm = normalizeSectionMap(sections);
    if (!norm) return;
    const key = Object.keys(norm)[0];
    if (key) syncUpsellFromSectionHtml(norm[key]);
  }

  function findNewCartCountEl(root) {
    if (!root) return null;
    if (root.nodeType === 1 && root.id === 'NewCartCount') return root;
    let el = root.querySelector ? root.querySelector('#NewCartCount') : null;
    if (el) return el;
    const templates = root.querySelectorAll
      ? root.querySelectorAll('template[data-sidebar-template], template')
      : [];
    for (let i = 0; i < templates.length; i++) {
      const tpl = templates[i];
      if (tpl.content) {
        el = tpl.content.querySelector('#NewCartCount');
        if (el) return el;
      }
    }
    return null;
  }

  function buildCartCountInnerHtml(count) {
    const n = Math.max(0, Number(count) || 0);
    if (n <= 0) return '';
    return (
      '<div class="cart-count' +
      (n > 9 ? ' cart-count--ellipse' : '') +
      '"><span aria-hidden="true">' +
      n +
      '</span></div>'
    );
  }

  function applyCartCountFromNode(countNode) {
    if (!countNode) return false;
    const clone = countNode.cloneNode(true);
    document.querySelectorAll('cart-count').forEach((el) => {
      el.replaceChildren(...clone.childNodes);
    });
    const hidden = findNewCartCountEl(getCartDrawer());
    if (hidden && hidden !== countNode) {
      hidden.replaceChildren(...countNode.cloneNode(true).childNodes);
    }
    return true;
  }

  function applyHeaderCartCount(count) {
    const n = Math.max(0, Number(count) || 0);
    const html = buildCartCountInnerHtml(n);
    document.querySelectorAll('cart-count').forEach((el) => {
      el.innerHTML = html;
    });
    const drawer = getCartDrawer();
    const hiddenCount = drawer && findNewCartCountEl(drawer);
    if (hiddenCount) {
      hiddenCount.innerHTML = html;
    }
  }

  function parseCountFromNewCartCountEl(el) {
    if (!el) return null;
    const span = el.querySelector('span[aria-hidden="true"]');
    if (span && span.textContent != null && span.textContent !== '') {
      const parsed = Number(span.textContent.trim());
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (!el.querySelector('.cart-count')) return 0;
    return null;
  }

  function parseCountFromSections(sections) {
    const norm = normalizeSectionMap(sections);
    if (!norm) return null;
    const html = norm[Object.keys(norm)[0]];
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('cart-drawer') || doc.body;
    return parseCountFromNewCartCountEl(findNewCartCountEl(root));
  }

  function resolveCartItemCount(data) {
    if (!data || typeof data !== 'object') return null;
    if (typeof data.item_count === 'number' && Number.isFinite(data.item_count)) {
      return data.item_count;
    }
    if (data.sections) {
      const fromSections = parseCountFromSections(data.sections);
      if (fromSections != null) return fromSections;
    }
    return null;
  }

  let cartCountVerifyTimer = null;
  let cartCountVerifyGen = 0;

  function scheduleCartCountVerify(delay) {
    clearTimeout(cartCountVerifyTimer);
    const gen = ++cartCountVerifyGen;
    cartCountVerifyTimer = setTimeout(() => {
      fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
        .then((r) => r.json())
        .then((cart) => {
          if (gen !== cartCountVerifyGen) return;
          applyHeaderCartCount(cart.item_count);
          syncDrawerEmptyState();
        })
        .catch(() => {});
    }, delay == null ? 120 : delay);
  }

  function syncHeaderCartCounts(fromNode, options) {
    const data = options && options.data;
    const explicitCount = options && typeof options.count === 'number' ? options.count : null;
    const resolved =
      explicitCount != null
        ? explicitCount
        : resolveCartItemCount(data);

    if (resolved != null) {
      applyHeaderCartCount(resolved);
    } else {
      const roots = [fromNode, getCartDrawer()].filter(Boolean);
      if (data && data.sections) {
        const norm = normalizeSectionMap(data.sections);
        if (norm) {
          const html = norm[Object.keys(norm)[0]];
          if (html) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            roots.push(doc.querySelector('cart-drawer') || doc.body);
          }
        }
      }
      for (let i = 0; i < roots.length; i++) {
        const countEl = findNewCartCountEl(roots[i]);
        if (countEl && applyCartCountFromNode(countEl)) {
          scheduleCartCountVerify(150);
          return;
        }
        const parsed = parseCountFromNewCartCountEl(countEl);
        if (parsed != null) {
          applyHeaderCartCount(parsed);
          scheduleCartCountVerify(150);
          return;
        }
      }
    }

    scheduleCartCountVerify(resolved != null ? 200 : 0);
  }

  async function refreshCartDrawerUI(data) {
    beginCartTotalsUpdate();
    try {
      syncHeaderCartCounts(null, { data });
      let applied = false;
      if (data && data.sections) {
        applied = applyDrawerFromSectionsPayload(data.sections);
        if (applied) syncUpsellFromSections(data.sections);
      }
      if (!applied) {
        const fallback = await fetchSectionHtmlPayload();
        if (fallback && fallback.sections) {
          applied = applyDrawerFromSectionsPayload(fallback.sections);
          if (applied) syncUpsellFromSections(fallback.sections);
          data = Object.assign({}, data || {}, fallback);
        }
      }
      syncHeaderCartCounts(null, { data });
      syncDrawerEmptyState();
      setTimeout(() => {
        syncDrawerEmptyState();
        syncHeaderCartCounts(null, { data });
        ensureCartDrawerUpsellMounted(true);
      }, 0);
      dispatchCartUpdated(data);
    } finally {
      await finishCartTotalsUpdate();
    }
  }

  async function fetchAndRefreshCartDrawer() {
    const fallback = await fetchSectionHtmlPayload();
    if (fallback) {
      await refreshCartDrawerUI(fallback);
    }
    return fallback;
  }

  // Own /cart/add.js calls must bypass the Candy Rack duplicate short-circuit.
  // Block ids are set before pre-add so CR CONTINUE can't double-add; without
  // this flag the pre-add's own fetch matches those ids and returns a fake
  // empty 200 — cart stays empty (knife-set-stand and other CR PDPs).
  let mcuOwnPreAddDepth = 0; // rm-fix-2026-10-18

  async function addItems(items, options) {
    const body = {
      items,
      sections: getSectionsRequestParam(),
      sections_url: window.location.pathname,
    };
    mcuOwnPreAddDepth += 1;
    let res;
    try {
      res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
    } finally {
      mcuOwnPreAddDepth = Math.max(0, mcuOwnPreAddDepth - 1);
    }
    if (!res.ok) {
      let err = { description: 'Unable to add to cart' };
      try { err = await res.json(); } catch (_) {}
      throw new Error(err.description || err.message || 'Unable to add to cart');
    }
    const data = await res.json();
    syncHeaderCartCounts(null, { data });
    await refreshCartDrawerUI(data);
    const skipOpenDrawer = options && options.skipOpenDrawer === true;
    if (!skipOpenDrawer) {
      closePresellPopupIfOpen();
    }
    dispatchRefresh({ openDrawer: !skipOpenDrawer });
    return data;
  }

  async function refreshAndOpenDrawer() {
    openCartDrawer();
    if (window.MiniCart && typeof window.MiniCart.refresh === 'function') {
      try {
        void window.MiniCart.refresh();
      } catch (_) {}
    }
  }

  /**
   * Native theme cart uses cart-drawer.purchaseHandler(ajaxPayload) to hydrate the *mounted* drawer.
   * Replacing #CartDrawer children only updates the inert <template> — keep this path in sync
   * with product-form-block.liquid.
   * @returns {boolean} true when the mounted drawer was updated
   */
  function applyDrawerFromSectionsPayload(sections) {
    const drawer = getCartDrawer();
    if (!drawer || typeof drawer.purchaseHandler !== 'function') return false;
    const norm = normalizeSectionMap(sections);
    if (!norm) return false;
    try {
      drawer.purchaseHandler({ sections: norm });
      scheduleSavingsRefresh(150);
      return true;
    } catch (_) {
      return false;
    }
  }

  async function fetchSectionHtmlPayload() {
    const drawer = getCartDrawer();
    if (!drawer) return null;
    const sectionId = getSectionId() || drawer.getAttribute('data-section-id') || 'cart-drawer';
    const res = await fetch('/?sections=' + encodeURIComponent(sectionId) + '&_=' + Date.now(), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    const json = await res.json();
    const html = json && (json[sectionId] || (Object.keys(json).length === 1 ? json[Object.keys(json)[0]] : null));
    if (!html) return null;
    return { sections: { [sectionId]: html } };
  }

  function getCartItemRemoveKey(cartItem) {
    if (!cartItem) return '';
    const lineKey = cartItem.getAttribute('data-line-key');
    if (lineKey) return 'key:' + lineKey;
    const index = cartItem.getAttribute('data-index');
    if (index != null && index !== '') return 'idx:' + index;
    const variantId = cartItem.getAttribute('data-variant-id');
    return variantId ? 'vid:' + variantId : '';
  }

  function findCartItemByRemoveKey(removeKey) {
    if (!removeKey) return null;
    const drawer = getCartDrawer();
    if (!drawer) return null;
    const esc =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape
        : (v) => String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    if (removeKey.indexOf('key:') === 0) {
      const lineKey = removeKey.slice(4);
      return drawer.querySelector(`cart-item[data-line-key="${esc(lineKey)}"]`);
    }
    if (removeKey.indexOf('idx:') === 0) {
      const index = removeKey.slice(4);
      return drawer.querySelector(`cart-item[data-index="${esc(index)}"]`);
    }
    if (removeKey.indexOf('vid:') === 0) {
      const variantId = removeKey.slice(4);
      return drawer.querySelector(`cart-item[data-variant-id="${esc(variantId)}"]`);
    }
    return null;
  }

  function setCartItemRemoving(cartItem, removing) {
    if (!cartItem) return;
    const body = cartItem.querySelector('[data-cart-item-body]') || cartItem;

    if (removing) {
      cartItem.classList.add('is-removing');
      cartItem.setAttribute('aria-busy', 'true');
      if (!cartItem.querySelector('.cart-item__removing-overlay')) {
        const overlay = document.createElement('div');
        overlay.className = 'cart-item__removing-overlay';
        overlay.setAttribute('role', 'status');
        overlay.innerHTML =
          '<span class="cart-item__removing-spinner" aria-hidden="true"></span>' +
          '<span class="visually-hidden">Removing item</span>';
        body.appendChild(overlay);
      }
      cartItem.querySelectorAll('cart-remove-button, quantity-btn, [data-quantity-input]').forEach((el) => {
        el.setAttribute('aria-disabled', 'true');
        if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
          el.disabled = true;
        }
      });
      return;
    }

    cartItem.classList.remove('is-removing');
    cartItem.removeAttribute('aria-busy');
    const overlay = cartItem.querySelector('.cart-item__removing-overlay');
    if (overlay) overlay.remove();
    cartItem.querySelectorAll('cart-remove-button, quantity-btn, [data-quantity-input]').forEach((el) => {
      el.removeAttribute('aria-disabled');
      if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
        el.disabled = false;
      }
    });
  }

  async function removeCartItemByVariantId(variantId, cartItem) {
    const lineKey = cartItem && cartItem.getAttribute('data-line-key');
    const lineIndex = cartItem && cartItem.getAttribute('data-index');
    const changeBody = {
      quantity: 0,
      sections: getSectionsRequestParam(),
      sections_url: window.location.pathname,
    };
    if (lineKey) {
      changeBody.id = lineKey;
    } else if (lineIndex != null && lineIndex !== '') {
      changeBody.line = Number(lineIndex) + 1;
    } else {
      changeBody.id = Number(variantId);
    }

    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(changeBody),
    });
    if (!res.ok) {
      throw new Error('Remove failed');
    }
    return res.json();
  }

  function enqueueRemoveAction(variantId, cartItem) {
    const removeKey = getCartItemRemoveKey(cartItem) || 'vid:' + variantId;
    if (removingCartItemKeys.has(removeKey)) {
      return removeQueue;
    }

    removingCartItemKeys.add(removeKey);
    setCartItemRemoving(cartItem, true);
    // Do NOT beginCartTotalsUpdate here — refreshCartDrawerUI already
    // begin/finish pairs. An extra begin left depth > 0 and skeletons stuck.

    removeQueue = removeQueue
      .then(async () => {
        const changeJson = await removeCartItemByVariantId(variantId, cartItem);
        if (changeJson && changeJson.sections) {
          await refreshCartDrawerUI(changeJson);
        } else {
          await fetchAndRefreshCartDrawer();
        }
        // purchaseHandler can no-op or paint a stale drawer (spinner stuck,
        // item still visible while /cart.js already dropped it). Always
        // re-pull sections when the line is still in the DOM.
        if (findCartItemByRemoveKey(removeKey)) {
          await fetchAndRefreshCartDrawer();
        }
        syncDrawerEmptyState();
        dispatchRefresh({ openDrawer: false });
        if (typeof window.MiniCartEnforceFreeGifts === 'function') {
          void window.MiniCartEnforceFreeGifts();
        }
      })
      .catch((err) => {
        console.error('[MiniCartUpsell] remove failed', err);
        const stillThere = findCartItemByRemoveKey(removeKey);
        if (stillThere) {
          setCartItemRemoving(stillThere, false);
          if (typeof stillThere.setDisabled === 'function') stillThere.setDisabled(false);
          if (typeof stillThere.setLoading === 'function') stillThere.setLoading(false);
        }
        // Recover UI even when change.js errors (e.g. gift-threshold race).
        return fetchAndRefreshCartDrawer().catch(() => {});
      })
      .finally(() => {
        removingCartItemKeys.delete(removeKey);
        const leftover = findCartItemByRemoveKey(removeKey);
        if (leftover) setCartItemRemoving(leftover, false);
        // Clear spinner overlays on every cart-item, not only the matched key
        // (section morph can leave a detached node / remounted twin).
        const drawer = getCartDrawer();
        if (drawer) {
          drawer.querySelectorAll('cart-item.is-removing').forEach((item) => {
            setCartItemRemoving(item, false);
          });
        }
        // Always clear loading so delete never leaves the drawer stuck.
        forceClearCartTotalsLoading();
        syncDrawerEmptyState();
      });
    return removeQueue;
  }

  function formatMoney(cents) {
    if (typeof window.MiniCartFormatMoney === 'function') {
      return window.MiniCartFormatMoney(cents);
    }
    return '$' + Math.round(Number(cents || 0) / 100);
  }

  function stripMoneyHtml(str) {
    if (str == null || str === '') return '';
    const s = String(str);
    if (s.indexOf('<') === -1) return s;
    const el = document.createElement('div');
    el.innerHTML = s;
    return (el.textContent || '').trim();
  }

  function getVariantDisplayPrice(variant, product) {
    if (variant && variant.price != null) {
      return formatMoney(variant.price);
    }
    return (
      stripMoneyHtml(variant && variant.priceFormatted) ||
      stripMoneyHtml(product && product.priceFormatted) ||
      ''
    );
  }

  function getVariantDisplayCompare(variant, product) {
    if (
      variant &&
      variant.compare_at_price != null &&
      Number(variant.compare_at_price) > Number(variant.price || 0)
    ) {
      return formatMoney(variant.compare_at_price);
    }
    const formatted =
      (variant && variant.compareAtPriceFormatted) ||
      (product && product.compareAtPriceFormatted);
    return stripMoneyHtml(formatted) || '';
  }

  let cartTotalsLoadingDepth = 0;
  let cartTotalsFinishTimer = null;
  let cartTotalsSafetyTimer = null;

  function getCartTotalsWrap() {
    const drawer = getCartDrawer();
    return drawer ? drawer.querySelector('[data-mcu-cart-totals]') : null;
  }

  function ensureCartTotalsOverlay(wrap) {
    if (!wrap) return;
    wrap.classList.add('mcu-cart-totals-wrap');
    // Skeletons are CSS-driven (Caraway-style); keep a screen-reader status only.
    if (wrap.querySelector('.mcu-cart-totals-overlay')) return;
    const overlay = document.createElement('div');
    overlay.className = 'mcu-cart-totals-overlay';
    overlay.setAttribute('role', 'status');
    overlay.innerHTML = '<span class="visually-hidden">Updating cart totals</span>';
    wrap.appendChild(overlay);
  }

  function clearCartTotalsLoadingUI() {
    const wrap = getCartTotalsWrap();
    if (wrap) {
      wrap.classList.remove('is-mcu-totals-loading');
      wrap.removeAttribute('aria-busy');
    }
    const drawer = getCartDrawer();
    if (!drawer) return;
    drawer.querySelectorAll('[data-cart-item-totals][is-loading]').forEach((el) => {
      el.removeAttribute('is-loading');
    });
    drawer.querySelectorAll('cart-item').forEach((item) => {
      if (item.classList.contains('is-removing')) {
        setCartItemRemoving(item, false);
      }
      try {
        if (typeof item.setDisabled === 'function') item.setDisabled(false);
        if (typeof item.setLoading === 'function') item.setLoading(false);
      } catch (_) {}
    });
  }

  function forceClearCartTotalsLoading() {
    cartTotalsLoadingDepth = 0;
    clearTimeout(cartTotalsFinishTimer);
    cartTotalsFinishTimer = null;
    clearTimeout(cartTotalsSafetyTimer);
    cartTotalsSafetyTimer = null;
    clearCartTotalsLoadingUI();
  }

  function beginCartTotalsUpdate() {
    cartTotalsLoadingDepth += 1;
    const wrap = getCartTotalsWrap();
    if (wrap) {
      ensureCartTotalsOverlay(wrap);
      wrap.classList.add('is-mcu-totals-loading');
      wrap.setAttribute('aria-busy', 'true');
    }
    // Caraway-style: skeleton every line price while qty/totals refresh
    const drawer = getCartDrawer();
    if (drawer) {
      drawer.querySelectorAll('[data-cart-item-totals]').forEach((el) => {
        el.setAttribute('is-loading', '');
      });
    }
    // Safety: never leave the drawer stuck if a finish is missed
    clearTimeout(cartTotalsSafetyTimer);
    cartTotalsSafetyTimer = setTimeout(() => {
      forceClearCartTotalsLoading();
    }, 4500);
  }

  function endCartTotalsUpdate() {
    cartTotalsLoadingDepth = Math.max(0, cartTotalsLoadingDepth - 1);
    if (cartTotalsLoadingDepth > 0) return;
    clearTimeout(cartTotalsSafetyTimer);
    cartTotalsSafetyTimer = null;
    clearCartTotalsLoadingUI();
  }

  async function finishCartTotalsUpdate() {
    try {
      await refreshCartTotalsDisplay();
    } finally {
      endCartTotalsUpdate();
    }
  }

  function scheduleCartTotalsFinish(delay) {
    clearTimeout(cartTotalsFinishTimer);
    cartTotalsFinishTimer = setTimeout(() => {
      void finishCartTotalsUpdate();
    }, delay == null ? 120 : delay);
  }

  async function refreshCartTotalsDisplay() {
    const drawer = getCartDrawer();
    if (!drawer) return;
    const savingsRow = drawer.querySelector('[data-mcu-savings-row]');
    const savingsAmount = drawer.querySelector('[data-mcu-savings-amount]');
    const totalEl = drawer.querySelector('[data-mcu-cart-total]');
    if (!savingsRow && !totalEl) return;

    const getCompareAtFromDom = (item) => {
      if (!item) return 0;
      const key = item.key != null ? String(item.key) : '';
      const variantId = item.variant_id != null ? String(item.variant_id) : '';
      const items = drawer.querySelectorAll('cart-item');
      let el = null;
      if (key) {
        el = Array.from(items).find((node) => node.getAttribute('data-line-key') === key) || null;
      }
      if (!el && variantId) {
        el = Array.from(items).find((node) => node.getAttribute('data-variant-id') === variantId) || null;
      }
      if (!el) return 0;
      return Number(el.getAttribute('data-mcu-compare-at') || 0);
    };

    try {
      const res = await fetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const cart = await res.json();
      if (typeof window.MiniCartApplyMoneyConfigFromCart === 'function') {
        window.MiniCartApplyMoneyConfigFromCart(cart);
      }

      if (totalEl) {
        totalEl.textContent = formatMoney(cart.total_price);
      }

      // /cart.js often omits compare-at — use cart fields when present, else Liquid data attrs on cart-item
      let totalSavings = 0;
      (cart.items || []).forEach((item) => {
        const discountSavings = Number(item.original_line_price || 0) - Number(item.final_line_price || 0);
        const compareAt = Number(
          item.compare_at_price || item.variant_compare_at_price || getCompareAtFromDom(item) || 0
        );
        const finalPrice = Number(item.final_price != null ? item.final_price : item.price || 0);
        const qty = Number(item.quantity || 1);
        let compareSavings = 0;
        if (compareAt > finalPrice) {
          compareSavings = (compareAt - finalPrice) * qty;
        }
        totalSavings += Math.max(0, discountSavings, compareSavings);
      });

      if (savingsRow && savingsAmount) {
        if (totalSavings > 0) {
          const beforeSavings = Number(cart.total_price || 0) + totalSavings;
          savingsAmount.textContent = formatMoney(beforeSavings);
          savingsRow.hidden = false;
          savingsRow.removeAttribute('hidden');
        } else {
          savingsAmount.textContent = '';
          savingsRow.hidden = true;
        }
      }
    } catch (_) {}
  }

  let savingsRefreshTimer = null;
  function runSavingsRefreshWhenIdle() {
    if (cartTotalsLoadingDepth > 0) {
      clearTimeout(savingsRefreshTimer);
      savingsRefreshTimer = setTimeout(() => {
        void runSavingsRefreshWhenIdle();
      }, 80);
      return;
    }
    void refreshCartTotalsDisplay();
  }

  function scheduleSavingsRefresh(delay) {
    clearTimeout(savingsRefreshTimer);
    savingsRefreshTimer = setTimeout(() => {
      void runSavingsRefreshWhenIdle();
    }, delay == null ? 120 : delay);
  }

  function setPopupTitle(text) {
    const popup = getPopup();
    if (!popup) return;
    const titleEl = popup.querySelector('[data-mcu-title]');
    if (titleEl) titleEl.textContent = text || 'People also bought';
  }

  function findMcuVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return null;
    const id = Number(variantId);
    return product.variants.find((v) => Number(v.id) === id) || null;
  }

  function applyMcuVariantToItem(article, product, variant) {
    if (!article || !variant) return;
    const img = article.querySelector('.mcu-item__image');
    if (img) {
      const src = variant.image || product.featuredImage || '';
      if (src) img.src = src;
      img.alt = variant.imageAlt || product.featuredImageAlt || product.title || '';
    }
    const price = article.querySelector('.mcu-item__price');
    if (price) {
      price.textContent = getVariantDisplayPrice(variant, product);
    }
    const compare = article.querySelector('.mcu-item__compare');
    if (compare) {
      const compareText = getVariantDisplayCompare(variant, product);
      if (compareText) {
        compare.textContent = compareText;
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }
  }

  function buildUpsellItem(product) {
    const article = document.createElement('article');
    article.className = 'mcu-item';

    let selectedVariantId = Number(product.firstVariantId || 0);
    const initialVariant = findMcuVariant(product, selectedVariantId);

    const img = document.createElement('img');
    img.className = 'mcu-item__image';
    img.src = (initialVariant && initialVariant.image) || product.featuredImage || '';
    img.alt =
      (initialVariant && initialVariant.imageAlt) ||
      product.featuredImageAlt ||
      product.title ||
      '';
    img.loading = 'lazy';
    article.appendChild(img);

    const details = document.createElement('div');
    details.className = 'mcu-item__details';

    const title = document.createElement('a');
    title.className = 'mcu-item__title';
    title.href = product.url || '#';
    title.target = '_blank';
    title.rel = 'noopener';
    title.textContent = product.title || '';
    details.appendChild(title);

    const priceRow = document.createElement('div');
    priceRow.className = 'mcu-item__price-row';
    const price = document.createElement('span');
    price.className = 'mcu-item__price';
    price.textContent = getVariantDisplayPrice(initialVariant, product);
    priceRow.appendChild(price);
    const compare = document.createElement('span');
    compare.className = 'mcu-item__compare';
    const initialCompare = getVariantDisplayCompare(initialVariant, product);
    if (initialCompare) {
      compare.textContent = initialCompare;
      priceRow.appendChild(compare);
    } else {
      compare.hidden = true;
      priceRow.appendChild(compare);
    }
    details.appendChild(priceRow);

    if (!product.hasOnlyDefaultVariant && Array.isArray(product.variants) && product.variants.length > 1) {
      const select = document.createElement('select');
      select.className = 'mcu-item__variant';
      select.setAttribute('aria-label', 'Variant for ' + product.title);
      product.variants.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        opt.textContent = v.title + (v.available ? '' : ' (sold out)');
        opt.disabled = !v.available;
        select.appendChild(opt);
      });
      select.value = String(selectedVariantId);
      select.addEventListener('change', (e) => {
        selectedVariantId = Number(e.target.value);
        const variant = findMcuVariant(product, selectedVariantId);
        if (variant) applyMcuVariantToItem(article, product, variant);
      });
      details.appendChild(select);
    }
    article.appendChild(details);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'mcu-item__add';
    addBtn.textContent = product.available === false ? 'Sold out' : 'Add';
    addBtn.disabled = product.available === false;
    addBtn.addEventListener('click', async () => {
      if (!selectedVariantId) return;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      try {
        const skipOpenDrawer = popupMode === 'cart-drawer';
        const mainProduct = window.__miniCartUpsellProduct || {};
        await trackAdd(
          addItems(
            [
              {
                id: selectedVariantId,
                quantity: 1,
                properties: {
                  _mcu_source: popupMode === 'cart-drawer' ? 'cart-drawer' : 'pdp-popup',
                  _mcu_from: mainProduct.handle || '',
                  _mcu_upsell: product.title || '',
                },
              },
            ],
            { skipOpenDrawer }
          )
        );
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              attributes: { mcu_popup: '1', mcu_from: mainProduct.handle || '' },
            }),
          });
        } catch (_) {}
        addBtn.textContent = 'Added';
        if (popupMode === 'cart-drawer') {
          setTimeout(() => closePresellPopupIfOpen(), 400);
        }
      } catch (err) {
        console.error('[MiniCartUpsell] upsell add failed', err);
        addBtn.textContent = 'Try again';
        addBtn.disabled = false;
      }
    });
    article.appendChild(addBtn);
    return article;
  }

  function renderUpsellListFromProducts(products) {
    const popup = getPopup();
    if (!popup) return;
    const list = popup.querySelector('[data-mcu-list]');
    const empty = popup.querySelector('[data-mcu-empty]');
    if (!list) return;
    list.querySelectorAll('.mcu-item').forEach((n) => n.remove());
    const available = (products || []).filter((p) => p && p.available !== false);
    if (!available.length) {
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    available.forEach((p) => list.appendChild(buildUpsellItem(p)));
  }

  function renderUpsellList() {
    const upsells = (window.__miniCartUpsellProduct && window.__miniCartUpsellProduct.upsellProducts) || [];
    renderUpsellListFromProducts(upsells);
  }

  function openPopup() {
    const popup = getPopup();
    if (!popup) return;
    popupMode = 'pdp';
    setPopupTitle('People also bought');
    renderUpsellList();
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mcu-locked');
  }

  function deriveProductOptions(product) {
    if (!product) return [];
    if (Array.isArray(product.options) && product.options.length) {
      return product.options;
    }
    const names = new Set();
    (product.variants || []).forEach((v) => {
      if (v.option1) names.add('option1');
      if (v.option2) names.add('option2');
      if (v.option3) names.add('option3');
    });
    const opts = [];
    ['option1', 'option2', 'option3'].forEach((key, idx) => {
      if (!names.has(key)) return;
      const values = [];
      (product.variants || []).forEach((v) => {
        const val = v[key];
        if (val && values.indexOf(val) === -1) values.push(val);
      });
      if (values.length) {
        opts.push({ name: 'Option ' + (idx + 1), position: idx + 1, values: values });
      }
    });
    return opts;
  }

  function getDefaultSelections(product) {
    const options = deriveProductOptions(product);
    const selections = {};
    const first =
      findMcuVariant(product, Number(product.firstVariantId)) ||
      (product.variants || []).find((v) => v.available) ||
      (product.variants || [])[0];
    if (!first) return selections;
    options.forEach((opt) => {
      const key = 'option' + opt.position;
      if (first[key]) selections[opt.name] = first[key];
    });
    return selections;
  }

  function variantMatchesSelections(variant, product, selections) {
    const options = deriveProductOptions(product);
    return options.every((opt) => {
      const key = 'option' + opt.position;
      const selected = selections[opt.name];
      if (!selected) return true;
      return variant[key] === selected;
    });
  }

  function findVariantForSelections(product, selections) {
    const variants = product.variants || [];
    return (
      variants.find((v) => variantMatchesSelections(v, product, selections)) ||
      variants.find((v) => Number(v.id) === Number(product.firstVariantId)) ||
      variants[0] ||
      null
    );
  }

  function isOptionValueAvailable(product, optionName, value, selections) {
    const test = Object.assign({}, selections, { [optionName]: value });
    return (product.variants || []).some((v) => v.available && variantMatchesSelections(v, product, test));
  }

  const MCU_IMAGE_MAIN_WIDTH = 1200;
  const MCU_IMAGE_THUMB_WIDTH = 240;

  function getDrawerVariantUi() {
    return window.__mcuDrawerVariantUi || {};
  }

  function imageUrlAtWidth(src, width) {
    if (!src) return '';
    let url = String(src).trim();
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/width=\d+/, 'width=' + width);
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + width;
  }

  /** Stable key for matching the same Shopify image across CDN URL formats. */
  function imageMatchKey(src) {
    if (!src) return '';
    let url = String(src).trim();
    if (url.indexOf('//') === 0) url = 'https:' + url;
    try {
      const path = new URL(url, window.location.origin).pathname;
      const file = path.split('/').pop() || '';
      return file
        .toLowerCase()
        .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|\d*x\d+)\./g, '.')
        .replace(/\.(jpg|jpeg|png|webp|gif).*$/i, '.$1');
    } catch (_) {
      return url.toLowerCase();
    }
  }

  function findImageIndexBySrc(images, activeSrc) {
    if (!images || !images.length) return 0;
    if (!activeSrc) return 0;
    const wantWidth = imageUrlAtWidth(activeSrc, MCU_IMAGE_MAIN_WIDTH);
    let idx = images.findIndex((img) => imageUrlAtWidth(img.src, MCU_IMAGE_MAIN_WIDTH) === wantWidth);
    if (idx >= 0) return idx;
    const wantKey = imageMatchKey(activeSrc);
    if (!wantKey) return 0;
    idx = images.findIndex((img) => imageMatchKey(img.src) === wantKey);
    return idx >= 0 ? idx : 0;
  }

  function isSwatchOption(optionName) {
    const ui = getDrawerVariantUi();
    const name = String(optionName || '').toLowerCase().trim();
    if (!name) return false;
    const primary = String(ui.primaryOptionName || '').toLowerCase().trim();
    if (primary && name === primary) return true;
    const secondary = Array.isArray(ui.secondaryOptionNames) ? ui.secondaryOptionNames : [];
    return secondary.some((n) => String(n || '').toLowerCase().trim() === name);
  }

  function resolveSwatchStyle(value) {
    const ui = getDrawerVariantUi();
    const colorDowncase = String(value || '').toLowerCase().trim();
    const patterns = String(ui.colorPatterns || '').split('\n');
    let isImage = false;
    let swatchValue = colorDowncase;
    patterns.forEach((line) => {
      const config = line.split('::');
      const label = String(config[0] || '').toLowerCase().trim();
      const patternValue = config[1];
      if (label !== colorDowncase || !patternValue) return;
      if (patternValue.indexOf('http') === -1 && /\.(png|jpe?g)/i.test(patternValue)) {
        swatchValue = patternValue;
        isImage = true;
      }
    });
    return { isImage, swatchValue };
  }

  function appendColorSwatch(pill, value) {
    const ui = getDrawerVariantUi();
    const { isImage, swatchValue } = resolveSwatchStyle(value);
    const shape = ui.primaryShape === 'round' ? 'round' : 'square';
    const swatch = document.createElement('shape-swatch');
    swatch.setAttribute('data-color', value);
    swatch.setAttribute('data-colors-patterns', ui.colorPatterns || '');
    swatch.className =
      'color-swatch pill__color-swatch ' + (shape === 'round' ? 'color-swatch--round' : 'color-swatch--square');
    if (isImage) {
      swatch.setAttribute('is-image', '');
      swatch.style.setProperty('--gsc-color-swatch-value', 'url(' + swatchValue + ')');
    }
    pill.appendChild(swatch);
  }

  function productUsesAltGallery(product) {
    if (!product) return false;
    if (product.useAltGallery === true) return true;
    const tags = Array.isArray(product.tags) ? product.tags : [];
    return tags.some((tag) => {
      const normalized = String(tag || '').toLowerCase().replace(/\s+/g, '');
      return normalized === 'alt-gallery' || normalized === 'altgallery';
    });
  }

  function normalizeMediaAlt(value) {
    return String(value || '').toLowerCase().trim();
  }

  function collectProductImages(product, variant) {
    const seen = new Set();
    const images = [];
    const add = (src, alt) => {
      const hiRes = imageUrlAtWidth(src, MCU_IMAGE_MAIN_WIDTH);
      if (!hiRes) return;
      const key = imageMatchKey(hiRes) || hiRes;
      if (seen.has(key)) return;
      seen.add(key);
      images.push({ src: hiRes, alt: alt || product.title || '' });
    };

    const productMedia = Array.isArray(product.media) ? product.media : [];
    const variantId = variant && variant.id ? String(variant.id).toLowerCase() : '';
    const option1 = normalizeMediaAlt(variant && variant.option1);

    if (productUsesAltGallery(product) && productMedia.length) {
      productMedia.forEach((media) => {
        const alt = normalizeMediaAlt(media && media.alt);
        if (!alt) return;
        if ((variantId && alt.indexOf(variantId) > -1) || (option1 && alt.indexOf(option1) > -1)) {
          add(media.src, media.alt);
        }
      });
      if (images.length) {
        if (variant && variant.image) {
          const variantKey = imageMatchKey(variant.image);
          const at = images.findIndex((img) => imageMatchKey(img.src) === variantKey);
          if (at > 0) {
            const [hit] = images.splice(at, 1);
            images.unshift(hit);
          } else if (at < 0) {
            images.unshift({
              src: imageUrlAtWidth(variant.image, MCU_IMAGE_MAIN_WIDTH),
              alt: variant.imageAlt || product.title || '',
            });
          }
        }
        return images;
      }
    }

    // Lead with the selected variant image so set-size / color clicks land on it.
    if (variant && variant.image) add(variant.image, variant.imageAlt);

    if (productMedia.length) {
      productMedia.forEach((media) => add(media && media.src, media && media.alt));
    } else {
      add(product.featuredImage, product.featuredImageAlt);
      (product.variants || []).forEach((v) => {
        if (v.image) add(v.image, v.imageAlt);
      });
    }
    return images;
  }

  function renderProductDrawerMedia(images, activeSrc) {
    const mediaRoot = getProductDrawer() && getProductDrawer().querySelector('[data-mcu-product-drawer-media]');
    if (!mediaRoot) return;

    const track =
      mediaRoot.querySelector('[data-mcu-media-track]') ||
      mediaRoot.querySelector('.mcu-product-drawer__media-track');
    const thumbsRoot = mediaRoot.querySelector('[data-mcu-media-thumbs]');
    const prevBtn = mediaRoot.querySelector('[data-mcu-media-prev]');
    const nextBtn = mediaRoot.querySelector('[data-mcu-media-next]');

    if (track) track.innerHTML = '';
    if (thumbsRoot) thumbsRoot.innerHTML = '';

    if (!track || !images.length) {
      mediaRoot.hidden = true;
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      if (thumbsRoot) thumbsRoot.hidden = true;
      return;
    }

    mediaRoot.hidden = false;
    track.setAttribute('role', 'group');
    track.setAttribute('aria-label', 'Product images');

    let activeIndex = findImageIndexBySrc(images, activeSrc || images[0].src);

    const slides = images.map((img, idx) => {
      const slide = document.createElement('div');
      slide.className = 'mcu-product-drawer__media-slide';
      slide.setAttribute('data-mcu-media-index', String(idx));
      slide.setAttribute('aria-hidden', idx === activeIndex ? 'false' : 'true');

      const image = document.createElement('img');
      image.className = 'mcu-product-drawer__media-img';
      image.loading = idx === activeIndex ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.alt = img.alt || '';
      image.src = imageUrlAtWidth(img.src, MCU_IMAGE_MAIN_WIDTH);
      image.sizes = '(max-width: 768px) 100vw, 420px';

      slide.appendChild(image);
      track.appendChild(slide);
      return slide;
    });

    const showNav = images.length > 1;
    if (prevBtn) prevBtn.hidden = !showNav;
    if (nextBtn) nextBtn.hidden = !showNav;

    let thumbButtons = [];
    if (showNav && thumbsRoot) {
      thumbsRoot.hidden = false;
      thumbButtons = images.map((img, idx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mcu-product-drawer__media-thumb' + (idx === activeIndex ? ' is-active' : '');
        btn.setAttribute('aria-label', 'View image');
        btn.setAttribute('aria-current', idx === activeIndex ? 'true' : 'false');
        btn.setAttribute('data-mcu-thumb-index', String(idx));

        const thumbImg = document.createElement('img');
        thumbImg.src = imageUrlAtWidth(img.src, MCU_IMAGE_THUMB_WIDTH);
        thumbImg.alt = img.alt || '';
        thumbImg.loading = 'lazy';
        btn.appendChild(thumbImg);

        btn.addEventListener('click', () => {
          setActiveIndex(idx, true);
        });

        thumbsRoot.appendChild(btn);
        return btn;
      });
    } else if (thumbsRoot) {
      thumbsRoot.hidden = true;
    }

    function updateAria() {
      slides.forEach((slide, idx) => {
        slide.setAttribute('aria-hidden', idx === activeIndex ? 'false' : 'true');
      });
    }

    function syncThumbs(shouldScrollThumb) {
      if (!thumbButtons.length) return;
      thumbButtons.forEach((btn, idx) => {
        const isActive = idx === activeIndex;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
      if (shouldScrollThumb) {
        const btn = thumbButtons[activeIndex];
        if (btn && typeof btn.scrollIntoView === 'function') {
          btn.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        }
      }
    }

    function scrollToIndex(idx, smooth) {
      const slide = slides[idx];
      if (!slide) return;
      const w = track.clientWidth || 1;
      track.scrollTo({
        left: idx * w,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }

    function setActiveIndex(idx, smooth) {
      const next = Math.max(0, Math.min(images.length - 1, Number(idx) || 0));
      if (next === activeIndex) return;
      activeIndex = next;
      updateAria();
      syncThumbs(true);
      scrollToIndex(activeIndex, smooth);
    }

    if (prevBtn) {
      prevBtn.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const next = activeIndex <= 0 ? images.length - 1 : activeIndex - 1;
        setActiveIndex(next, true);
      };
    }
    if (nextBtn) {
      nextBtn.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const next = activeIndex >= images.length - 1 ? 0 : activeIndex + 1;
        setActiveIndex(next, true);
      };
    }

    updateAria();
    syncThumbs(false);
    scrollToIndex(activeIndex, false);

    let raf = null;
    track.onscroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const w = track.clientWidth || 1;
        const idx = Math.round(track.scrollLeft / w);
        const next = Math.max(0, Math.min(images.length - 1, idx));
        if (next !== activeIndex) {
          activeIndex = next;
          updateAria();
          syncThumbs(false);
        }
      });
    };
  }

  function renderProductDrawerOptions(product, selections) {
    const optionsRoot =
      getProductDrawer() && getProductDrawer().querySelector('[data-mcu-product-drawer-options]');
    if (!optionsRoot) return;
    optionsRoot.innerHTML = '';
    const options = deriveProductOptions(product);
    if (!options.length || product.hasOnlyDefaultVariant) {
      optionsRoot.hidden = true;
      return;
    }
    optionsRoot.hidden = false;

    options.forEach((opt) => {
      const group = document.createElement('div');
      group.className = 'mcu-product-drawer__option';
      const label = document.createElement('div');
      label.className = 'product-variant-picker__option-label';
      label.textContent = opt.name + ':';
      group.appendChild(label);

      const list = document.createElement('div');
      list.className = 'product-variant-picker__pill-list';
      const isSwatch = isSwatchOption(opt.name);
      (opt.values || []).forEach((value) => {
        const wrap = document.createElement('div');
        const pill = document.createElement('button');
        pill.type = 'button';
        const selected = selections[opt.name] === value;
        const available = isOptionValueAvailable(product, opt.name, value, selections);
        pill.className =
          'pill pill--large pill--solid' +
          (isSwatch ? ' pill--with-color-swatch-in-product-section' : '') +
          (selected ? ' selected' : '') +
          (!available ? ' is-unavailable' : '');
        pill.disabled = !available;
        pill.setAttribute('aria-label', value);
        pill.setAttribute('data-mcu-product-option', opt.name);
        pill.setAttribute('data-mcu-product-option-value', value);
        if (isSwatch) appendColorSwatch(pill, value);
        const span = document.createElement('span');
        span.className = 'pill__label';
        span.textContent = value;
        pill.appendChild(span);
        wrap.appendChild(pill);
        list.appendChild(wrap);
      });
      group.appendChild(list);
      optionsRoot.appendChild(group);
    });
  }

  function applyProductDrawerVariant(product, variant) {
    activeProductDrawerVariantId = variant ? Number(variant.id) : null;
    const drawer = getProductDrawer();
    if (!drawer || !variant) return;

    const priceEl = drawer.querySelector('[data-mcu-product-drawer-price]');
    const compareEl = drawer.querySelector('[data-mcu-product-drawer-compare]');
    const priceRow = drawer.querySelector('[data-mcu-product-drawer-price-row]');
    const atc = drawer.querySelector('[data-mcu-product-drawer-atc]');
    const atcLabel = drawer.querySelector('[data-mcu-product-drawer-atc-label]');

    if (priceRow) priceRow.hidden = false;
    if (priceEl) priceEl.textContent = getVariantDisplayPrice(variant, product);
    if (compareEl) {
      const compareText = getVariantDisplayCompare(variant, product);
      if (compareText) {
        compareEl.textContent = compareText;
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    const productImages = collectProductImages(product, variant);
    // Always prefer the selected variant's featured image so set-size changes update the gallery.
    const activeImage =
      (variant && variant.image) ||
      (productImages[0] && productImages[0].src) ||
      product.featuredImage;
    renderProductDrawerMedia(productImages, activeImage);

    if (atc) {
      const soldOut = variant.available === false;
      atc.disabled = soldOut || atc.classList.contains('is-loading');
      if (atcLabel) {
        atcLabel.textContent = soldOut ? 'Sold out' : 'Add to cart';
      }
    }
  }

  function teardownProductDrawerPicker() {
    if (productDrawerPickerObserver) {
      productDrawerPickerObserver.disconnect();
      productDrawerPickerObserver = null;
    }
    productDrawerUsesPdpPicker = false;
  }

  function activateScriptsInElement(root) {
    if (!root) return;
    root.querySelectorAll('script').forEach((oldScript) => {
      const script = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
      });
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  }

  function getPickerVariantsFromHost(host, product) {
    const script = host && host.querySelector('script[data-variant-picker-variants]');
    if (script && script.textContent) {
      try {
        const parsed = JSON.parse(script.textContent);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {}
    }
    return (product && product.variants) || [];
  }

  function normalizePdpVariant(variant) {
    if (!variant) return null;
    let imageSrc = '';
    let imageAlt = variant.title || '';
    if (variant.featured_image && variant.featured_image.src) {
      imageSrc = variant.featured_image.src;
      imageAlt = variant.featured_image.alt || imageAlt;
    } else if (variant.featured_media && variant.featured_media.preview_image) {
      imageSrc = variant.featured_media.preview_image.src || '';
      imageAlt = variant.featured_media.alt || imageAlt;
    } else if (variant.image) {
      imageSrc = variant.image;
      imageAlt = variant.imageAlt || imageAlt;
    }
    if (imageSrc) imageSrc = imageUrlAtWidth(imageSrc, MCU_IMAGE_MAIN_WIDTH);
    return {
      id: variant.id,
      available: variant.available !== false,
      price: variant.price,
      compare_at_price: variant.compare_at_price,
      image: imageSrc,
      imageAlt: imageAlt,
      option1: variant.option1,
      option2: variant.option2,
      option3: variant.option3,
    };
  }

  function stripWhatsIncludedFromPicker(root) {
    if (!root) return;
    root.querySelectorAll('.selected-variant-popup, .selected-included').forEach((el) => {
      el.remove();
    });
    root.querySelectorAll('.selected-name-swatch').forEach((el) => el.remove());
    root.querySelectorAll('[data-variant-picker-option-labels]').forEach((wrap) => {
      wrap.querySelectorAll('sidebar-button, .product-variant-picker__popup-toggle').forEach((el) => {
        el.remove();
      });
    });
  }

  function getSelectionsFromPickerHost(host) {
    const selections = {};
    if (!host) return selections;
    host.querySelectorAll('[data-variant-picker-option-inputs] input[type="radio"]:checked').forEach(
      (input) => {
        const name = input.getAttribute('name');
        const value = input.getAttribute('value');
        if (name && value) selections[name] = value;
      }
    );

    // Color swatches may only flip label.selected; keep option1 in sync from labels.
    host.querySelectorAll('[data-variant-picker-option-labels]').forEach((labelGroup) => {
      const optionName = labelGroup.getAttribute('option-picker-name');
      if (!optionName || selections[optionName]) return;
      const selectedLabel =
        labelGroup.querySelector('[data-color-swatches-picker-label].selected') ||
        labelGroup.querySelector('.color-swatch-select-parent.selected') ||
        labelGroup.querySelector('label.selected[data-value]');
      if (!selectedLabel) return;
      const value =
        selectedLabel.getAttribute('data-value') ||
        selectedLabel.getAttribute('data-value2') ||
        (selectedLabel.querySelector('span') && selectedLabel.querySelector('span').textContent.trim());
      if (value) selections[optionName] = value;
    });

    // Set-size cards: read checked product-vo radio value by option position/name.
    host.querySelectorAll('.product-vo-grid[data-option-position]').forEach((grid) => {
      if (grid.style && grid.style.display === 'none') return;
      const checked = grid.querySelector('input[type="radio"]:checked');
      if (!checked) return;
      const name = checked.getAttribute('data-option-name') || checked.getAttribute('name');
      const value = checked.getAttribute('data-value2') || checked.getAttribute('value');
      if (!name || !value) return;
      // Prefer human option names from label groups when radio name is custom_radio_*.
      const pos = grid.getAttribute('data-option-position');
      const labelGroups = host.querySelectorAll('[data-variant-picker-option-labels]');
      const labelGroup = pos ? labelGroups[Number(pos) - 1] : null;
      const optionName =
        (labelGroup && labelGroup.getAttribute('option-picker-name')) ||
        (name.indexOf('custom_radio_') === 0 ? null : name);
      if (optionName) selections[optionName] = value;
    });

    return selections;
  }

  function findVariantInPdpList(variants, product, selections) {
    if (!variants || !variants.length) return null;
    const options = deriveProductOptions(product);
    if (!options.length) return variants.find((v) => v.available) || variants[0];

    const match = variants.find((v) =>
      options.every((opt) => {
        const selected = selections[opt.name];
        if (!selected) return true;
        const key = 'option' + opt.position;
        return v[key] === selected;
      })
    );
    return match || null;
  }

  function resolveVariantFromPickerHost(host, product) {
    const variants = getPickerVariantsFromHost(host, product);
    const selections = getSelectionsFromPickerHost(host);
    let raw = findVariantInPdpList(variants, product, selections);

    if (!raw) {
      const variantId = readSelectedVariantIdFromPickerHost(host);
      if (variantId) raw = variants.find((v) => Number(v.id) === Number(variantId));
    }

    if (!raw && selections && Object.keys(selections).length) {
      raw = findVariantForSelections(product, selections);
    }

    // Last resort: keep previous drawer variant's color when only set size changed.
    if (!raw && activeProductDrawerVariantId) {
      const prev = variants.find((v) => Number(v.id) === Number(activeProductDrawerVariantId));
      if (prev && selections) {
        const options = deriveProductOptions(product);
        const merged = {};
        options.forEach((opt) => {
          const key = 'option' + opt.position;
          merged[opt.name] = selections[opt.name] || prev[key];
        });
        raw = findVariantInPdpList(variants, product, merged);
      }
    }

    return raw || null;
  }

  function readSelectedVariantIdFromPickerHost(host) {
    if (!host) return null;
    const picker = host.querySelector('variant-picker');
    if (picker) {
      const fromAttr = Number(picker.getAttribute('data-selected-variant-id'));
      if (Number.isFinite(fromAttr) && fromAttr > 0) return fromAttr;
    }
    const checkedInput = host.querySelector('.product-vo-item input[type="radio"]:checked');
    if (checkedInput) {
      const item = checkedInput.closest('.product-vo-item');
      const priceNode = item && item.querySelector('[data-variant-id]');
      if (priceNode) {
        const fromPrice = Number(priceNode.getAttribute('data-variant-id'));
        if (Number.isFinite(fromPrice) && fromPrice > 0) return fromPrice;
      }
    }
    return null;
  }

  function syncProductDrawerPickerLabels(host) {
    if (!host) return;
    const inputGroups = host.querySelectorAll('[data-variant-picker-option-inputs]');
    const labelGroups = host.querySelectorAll('[data-variant-picker-option-labels]');

    inputGroups.forEach((inputGroup, idx) => {
      const labelGroup = labelGroups[idx];
      if (!labelGroup) return;

      const checked = inputGroup.querySelector('input[type="radio"]:checked');
      const checkedId = checked ? checked.id : '';
      const checkedValue = checked
        ? checked.getAttribute('data-value') || checked.value || ''
        : '';

      labelGroup.querySelectorAll('label[for]').forEach((label) => {
        const isSelected = !!(checkedId && label.getAttribute('for') === checkedId);
        label.classList.toggle('selected', isSelected);
      });

      labelGroup.querySelectorAll('[data-color-swatches-picker-label]').forEach((label) => {
        const labelValue = label.getAttribute('data-value') || '';
        const isSelected =
          !!(checkedId && label.getAttribute('for') === checkedId) ||
          !!(checkedValue && labelValue && labelValue === checkedValue);
        label.classList.toggle('selected', isSelected);
      });

      labelGroup.querySelectorAll('color-swatches-picker').forEach((picker) => {
        if (typeof picker.updateSelected === 'function') {
          picker.updateSelected();
          return;
        }
        const activeEl = picker.querySelector('[data-color-swatches-picker-active-value]');
        if (!activeEl) return;
        const selectedLabel = labelGroup.querySelector(
          '[data-color-swatches-picker-label].selected'
        );
        if (selectedLabel) {
          const display =
            selectedLabel.getAttribute('data-value') ||
            selectedLabel.querySelector('span')?.textContent?.trim();
          if (display) activeEl.textContent = display;
        } else if (checkedValue) {
          activeEl.textContent = checkedValue;
        }
      });

      const dropdownLabel = labelGroup.querySelector('[data-dropdown-variant-picker-label]');
      if (dropdownLabel && checkedValue) {
        const span = dropdownLabel.querySelector('span');
        if (span) span.textContent = checkedValue;
      }
    });
  }

  function syncProductDrawerFromPdpPicker(host, product) {
    if (!host || !product) return;
    syncProductDrawerPickerLabels(host);
    const raw = resolveVariantFromPickerHost(host, product);
    if (!raw) return;
    const normalized = normalizePdpVariant(raw);
    if (normalized) applyProductDrawerVariant(product, normalized);
  }

  function wireInjectedVariantPicker(host, product) {
    teardownProductDrawerPicker();
    if (!host || !product) return;
    productDrawerUsesPdpPicker = true;

    const drawer = getProductDrawer();
    const priceRow = drawer && drawer.querySelector('[data-mcu-product-drawer-price-row]');
    if (host.querySelector('.product-vo-wrapper') && priceRow) {
      priceRow.hidden = true;
    }

    const sync = () => syncProductDrawerFromPdpPicker(host, product);

    host.addEventListener(
      'change',
      (event) => {
        if (event.target && event.target.matches('input[type="radio"]')) {
          sync();
        } else {
          setTimeout(sync, 80);
        }
      },
      true
    );
    host.addEventListener(
      'click',
      (event) => {
        if (
          event.target.closest(
            '[data-color-swatches-picker-label], .color-swatch-select-parent, .product-vo-item, .product-vo-label, .variant-picker-dropdown__item, .swatcher-holder label, label.color-swatch-select-parent'
          )
        ) {
          setTimeout(sync, 0);
          setTimeout(sync, 120);
          setTimeout(sync, 350);
        }
      },
      true
    );

    const picker = host.querySelector('variant-picker');
    if (picker) {
      productDrawerPickerObserver = new MutationObserver(() => sync());
      productDrawerPickerObserver.observe(picker, {
        attributes: true,
        attributeFilter: ['data-selected-variant-id'],
      });
    }

    setTimeout(sync, 200);
    setTimeout(sync, 600);
  }

  async function loadPdpVariantPicker(product) {
    const optionsRoot =
      getProductDrawer() && getProductDrawer().querySelector('[data-mcu-product-drawer-options]');
    if (!optionsRoot || !product) return false;

    if (product.hasOnlyDefaultVariant) {
      optionsRoot.hidden = true;
      optionsRoot.innerHTML = '';
      return false;
    }

    optionsRoot.hidden = false;
    optionsRoot.innerHTML =
      '<div class="mcu-product-drawer__picker-loading" role="status" aria-live="polite" aria-busy="true">' +
      '<span class="mcu-product-drawer__picker-spinner" aria-hidden="true"></span>' +
      '<span class="mcu-product-drawer__picker-loading-text">Loading options…</span>' +
      '</div>';

    const productUrl =
      product.url || (product.handle ? '/products/' + encodeURIComponent(product.handle) : '');
    if (!productUrl) {
      optionsRoot.innerHTML = '';
      renderProductDrawerOptions(product, getDefaultSelections(product));
      return false;
    }

    try {
      const res = await fetch(productUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('Product page fetch failed');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const picker =
        (product.id &&
          doc.querySelector(
            'variant-picker.product-variant-picker[data-product-id="' + product.id + '"]'
          )) ||
        doc.querySelector('variant-picker.product-variant-picker') ||
        doc.querySelector('variant-picker');

      if (!picker) throw new Error('Variant picker not found on product page');

      const clone = picker.cloneNode(true);
      clone.removeAttribute('id');
      clone.setAttribute('data-update-url', 'false');
      clone.setAttribute('data-mcu-drawer-picker', 'true');
      stripWhatsIncludedFromPicker(clone);

      optionsRoot.innerHTML = '';
      const host = document.createElement('div');
      host.className = 'mcu-product-drawer__pdp-picker custom-product pdp-flat';
      host.setAttribute('data-mcu-pdp-picker-host', '');
      host.appendChild(clone);
      optionsRoot.appendChild(host);

      activateScriptsInElement(host);
      wireInjectedVariantPicker(host, product);
      return true;
    } catch (err) {
      console.warn('[MiniCartUpsell] PDP picker load failed, using fallback', err);
      productDrawerUsesPdpPicker = false;
      renderProductDrawerOptions(product, getDefaultSelections(product));
      return false;
    }
  }

  function refreshProductDrawerSelections() {
    if (!activeProductDrawerProduct) return;
    const host =
      getProductDrawer() &&
      getProductDrawer().querySelector('[data-mcu-pdp-picker-host]');
    if (productDrawerUsesPdpPicker && host) {
      syncProductDrawerFromPdpPicker(host, activeProductDrawerProduct);
      return;
    }
    const variant = findVariantForSelections(
      activeProductDrawerProduct,
      activeProductDrawerSelections
    );
    renderProductDrawerOptions(activeProductDrawerProduct, activeProductDrawerSelections);
    applyProductDrawerVariant(activeProductDrawerProduct, variant);
  }

  async function renderProductDrawerContent(product) {
    const drawer = getProductDrawer();
    if (!drawer || !product) return;
    teardownProductDrawerPicker();
    activeProductDrawerProduct = product;
    activeProductDrawerSelections = getDefaultSelections(product);

    const titleEl = drawer.querySelector('[data-mcu-product-drawer-title]');
    if (titleEl) titleEl.textContent = product.title || '';

    const viewLink = drawer.querySelector('[data-mcu-product-drawer-view-product]');
    if (viewLink) {
      const detailsUrl =
        product.url || (product.handle ? '/products/' + product.handle : '');
      if (detailsUrl) {
        viewLink.href = detailsUrl;
        viewLink.hidden = false;
      } else {
        viewLink.hidden = true;
      }
    }

    const initialVariant =
      findMcuVariant(product, Number(product.firstVariantId)) ||
      findVariantForSelections(product, activeProductDrawerSelections);
    if (initialVariant) {
      applyProductDrawerVariant(product, initialVariant);
    }

    await loadPdpVariantPicker(product);
  }

  function isProductDrawerOpen() {
    const drawer = getProductDrawer();
    return !!(drawer && drawer.classList.contains('is-open'));
  }

  function resetProductDrawerScroll() {
    const panel = getProductDrawer();
    if (!panel) return;
    const body = panel.querySelector('.mcu-product-drawer__body');
    if (body) body.scrollTop = 0;
    const options = panel.querySelector('[data-mcu-product-drawer-options]');
    if (options) options.scrollTop = 0;
    panel.querySelectorAll('.mcu-product-drawer__media-thumbs').forEach((el) => {
      el.scrollLeft = 0;
    });
  }

  function openProductDrawer(product) {
    if (!product) return;
    if (!isCartDrawerOpen()) {
      openCartDrawer();
    }
    resetProductDrawerScroll();
    void renderProductDrawerContent(product).then(() => {
      resetProductDrawerScroll();
    });
    const panel = getProductDrawer();
    const host = getProductDrawerHost();
    if (!panel || !host) return;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    host.setAttribute('data-mcu-product-drawer-open', '');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.add('is-open');
        resetProductDrawerScroll();
      });
    });
  }

  function closeProductDrawer() {
    const panel = getProductDrawer();
    const host = getProductDrawerHost();
    if (!panel) return;
    resetProductDrawerScroll();
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    if (host) host.removeAttribute('data-mcu-product-drawer-open');
    activeProductDrawerProduct = null;
    activeProductDrawerSelections = {};
    activeProductDrawerVariantId = null;
    teardownProductDrawerPicker();

    const finishHide = () => {
      if (!panel.classList.contains('is-open')) {
        panel.hidden = true;
        resetProductDrawerScroll();
      }
    };
    const slidePanel = panel.querySelector('.mcu-product-drawer__panel') || panel;
    const onEnd = (e) => {
      if (e.target !== slidePanel || e.propertyName !== 'transform') return;
      slidePanel.removeEventListener('transitionend', onEnd);
      finishHide();
    };
    slidePanel.addEventListener('transitionend', onEnd);
    setTimeout(finishHide, MCU_SLIDE_MS + 80);
  }

  function setProductDrawerAtcLoading(loading) {
    const atc = getProductDrawer() && getProductDrawer().querySelector('[data-mcu-product-drawer-atc]');
    if (!atc) return;
    atc.classList.toggle('is-loading', loading);
    if (loading) {
      atc.disabled = true;
      atc.setAttribute('aria-busy', 'true');
    } else {
      atc.removeAttribute('aria-busy');
      const variant = findMcuVariant(activeProductDrawerProduct, activeProductDrawerVariantId);
      atc.disabled = !(variant && variant.available !== false);
    }
  }

  async function handleProductDrawerAdd() {
    if (!activeProductDrawerVariantId) return;
    const atc = getProductDrawer() && getProductDrawer().querySelector('[data-mcu-product-drawer-atc]');
    if (atc && atc.dataset.mcuAdding === 'true') return;
    if (atc) atc.dataset.mcuAdding = 'true';
    setProductDrawerAtcLoading(true);
    try {
      await trackAdd(
        addItems([{ id: activeProductDrawerVariantId, quantity: 1 }], { skipOpenDrawer: true })
      );
      closeProductDrawer();
    } catch (err) {
      console.error('[MiniCartUpsell] product drawer add failed', err);
    } finally {
      if (atc) delete atc.dataset.mcuAdding;
      setProductDrawerAtcLoading(false);
      refreshProductDrawerSelections();
    }
  }

  function wireProductDrawerControls() {
    if (window.__mcuProductDrawerWired) return;
    window.__mcuProductDrawerWired = true;

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-mcu-product-drawer-back]')) {
        event.preventDefault();
        closeProductDrawer();
        return;
      }

      const pill = event.target.closest('[data-mcu-product-option]:not(:disabled)');
      if (
        pill &&
        pill.closest('#' + PRODUCT_DRAWER_ID) &&
        !productDrawerUsesPdpPicker
      ) {
        event.preventDefault();
        if (!activeProductDrawerProduct || pill.disabled) return;
        const optionName = pill.getAttribute('data-mcu-product-option');
        const value = pill.getAttribute('data-mcu-product-option-value');
        if (!optionName || !value) return;
        activeProductDrawerSelections[optionName] = value;
        refreshProductDrawerSelections();
        return;
      }

      if (event.target.closest('[data-mcu-product-drawer-atc]')) {
        event.preventDefault();
        void handleProductDrawerAdd();
      }
    });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !isProductDrawerOpen()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        closeProductDrawer();
      },
      true
    );
  }

  function findCartDrawerUpsellProduct(productId) {
    const list = window.__cartDrawerUpsellProducts;
    if (!Array.isArray(list)) return null;
    const id = Number(productId);
    return list.find((p) => p && Number(p.id) === id) || null;
  }

  function productNeedsVariantPicker(product) {
    if (!product) return false;
    if (product.hasOnlyDefaultVariant === true) return false;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.length > 1;
  }

  function setCartDrawerUpsellButtonLoading(btn, loading) {
    if (!btn) return;
    btn.classList.toggle('is-loading', loading);
    btn.disabled = loading;
    if (loading) {
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.removeAttribute('aria-busy');
    }
  }

  async function handleDirectCartDrawerAdd(btn, variantId) {
    if (!btn || !variantId || btn.dataset.mcuAdding === 'true') return;
    btn.dataset.mcuAdding = 'true';
    setCartDrawerUpsellButtonLoading(btn, true);
    try {
      await trackAdd(addItems([{ id: variantId, quantity: 1 }], { skipOpenDrawer: true }));
    } catch (err) {
      console.error('[MiniCartUpsell] cart drawer direct add failed', err);
    } finally {
      delete btn.dataset.mcuAdding;
      setCartDrawerUpsellButtonLoading(btn, false);
    }
  }

  function isPresellPopupOpen() {
    const popup = getPopup();
    return !!(popup && popup.getAttribute('aria-hidden') === 'false');
  }

  function closePresellPopupIfOpen() {
    if (!isPresellPopupOpen()) return;
    closePopupOnly();
    shouldOpenCartOnClose = false;
  }

  function closePopupOnly() {
    const popup = getPopup();
    if (!popup) return;
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mcu-locked');
    if (popupMode === 'cart-drawer') popupMode = 'pdp';
  }

  async function closePopupAndOpenDrawer() {
    closePopupOnly();
    if (popupMode === 'cart-drawer' || !shouldOpenCartOnClose) {
      popupMode = 'pdp';
      return;
    }
    try {
      await flushPendingAdds();
    } catch (_) {}
    await refreshAndOpenDrawer();
    pendingMain = null;
    shouldOpenCartOnClose = false;
  }

  function wirePopupControls() {
    const popup = getPopup();
    if (!popup || popup.dataset.mcuWired === 'true') return;
    popup.dataset.mcuWired = 'true';
    popup.addEventListener('click', (e) => {
      if (e.target && e.target.matches('[data-mcu-close]')) {
        e.preventDefault();
        closePopupAndOpenDrawer();
        return;
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || popup.getAttribute('aria-hidden') !== 'false') return;
      if (isProductDrawerOpen()) return;
      closePopupAndOpenDrawer();
    });
  }

  function resolveVariantId(form) {
    const input = form && form.querySelector('input[name="id"]');
    const val = input && input.value ? Number(input.value) : NaN;
    return Number.isFinite(val) ? val : null;
  }

  /**
   * Aligned with `find_visible_addon` in `product-form-block.liquid`.
   * Pills in `product-add-on-block` update the visible image but often never
   * set `input.bundle-checkbox:checked`, so we must read `data-variant-id` from
   * the shown `.addon-variant-image` when the bundle is "selected".
   */
  function getVisibleJazzupAddonVariantNode(container) {
    if (!container) return null;
    let el = container.querySelector('.addon-variant-image:not([style*="display: none"])');
    if (!el) {
      el = container.querySelector('.addon-variant-image.add-on-selected-img');
    }
    if (!el) {
      const all = container.querySelectorAll('.addon-variant-image');
      if (all.length) el = all[0];
    }
    return el;
  }

  function parseVariantIdFromNode(node) {
    if (!node) return null;
    const raw = node.getAttribute('data-variant-id') || node.dataset.variantId;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Same rules as `product-form-block` add-on path: if the upsell layer
   * intercepts ATC first, we must add main + add-on(s) in one /cart/add.js call.
   * Supports legacy `#addon-container` and multi-block `.product__bundle_main.add-ons.selected`.
   */
  function getSelectedJazzupAddonContainers() {
    const containers = [];
    const legacy = document.getElementById('addon-container');
    if (legacy && legacy.classList.contains('selected')) {
      containers.push(legacy);
    }
    document.querySelectorAll('.product__bundle_main.add-ons.selected').forEach((container) => {
      if (legacy && container === legacy) return;
      containers.push(container);
    });
    return containers;
  }

  function getAddonVariantIdFromContainer(addonContainer) {
    if (!addonContainer) return null;
    const checked = addonContainer.querySelector('input.bundle-checkbox:checked');
    if (checked) {
      const raw =
        checked.dataset.variantId != null && checked.dataset.variantId !== ''
          ? checked.dataset.variantId
          : checked.value;
      if (raw != null && raw !== '') {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
    return parseVariantIdFromNode(getVisibleJazzupAddonVariantNode(addonContainer));
  }

  function getSelectedJazzupAddonVariantId() {
    const containers = getSelectedJazzupAddonContainers();
    if (!containers.length) return null;
    return getAddonVariantIdFromContainer(containers[0]);
  }

  function getSelectedJazzupAddonLineItems() {
    const lineItems = [];
    const seen = new Set();
    getSelectedJazzupAddonContainers().forEach((container) => {
      const id = getAddonVariantIdFromContainer(container);
      if (!id || seen.has(String(id))) return;
      seen.add(String(id));
      let quantity = 1;
      const qtyInput = container.querySelector('[data-addon-quantity-input]');
      if (qtyInput) {
        quantity = parseInt(qtyInput.value, 10) || 0;
      }
      if (quantity > 0) {
        lineItems.push({ id, quantity });
      }
    });
    return lineItems;
  }

  function getFormQuantity(form) {
    const input = form && form.querySelector('input[name="quantity"]');
    const qty = input ? parseInt(input.value, 10) : NaN;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  function buildAtcLineItems(mainVariantId, mainQuantity) {
    const mainQty = Number.isFinite(mainQuantity) && mainQuantity > 0 ? mainQuantity : 1;
    const lineItems = [{ id: mainVariantId, quantity: mainQty }];
    getSelectedJazzupAddonLineItems().forEach((item) => {
      lineItems.push(item);
    });
    return lineItems;
  }

  function resetJazzupAddonSelection() {
    getSelectedJazzupAddonContainers().forEach((addonContainer) => {
      addonContainer.classList.remove('selected');
      addonContainer.querySelectorAll('input.bundle-checkbox').forEach((radio) => {
        radio.checked = false;
      });
      addonContainer.querySelectorAll('.addon-variant-image').forEach((img) => {
        img.classList.remove('selected');
      });
    });
  }

  function isQuickAddForm(form) {
    if (!form) return false;
    return !!(
      form.hasAttribute('data-qa-form') ||
      form.classList.contains('quick-add-modal__form') ||
      form.closest('.quick-add-modal') ||
      form.closest('[data-quick-add-modal]') ||
      form.closest('[data-quick-add-host]')
    );
  }

  function isMainPdpForm(form) {
    if (!form) return false;
    // Quick view / overhaul quick-add must never go through Candy Rack pre-add
    // (that path hard-codes quantity: 1 and swallows the modal submit).
    if (isQuickAddForm(form)) return false;
    if (form.closest('.product__bundle_main')) return false;
    // Ava redesign PDP owns its Candy Rack bridge (steak/serving multi-item add).
    if (form.hasAttribute('data-pdp-form') || form.hasAttribute('data-ava-candy-bridge')) {
      return false;
    }

    const productFormEl = form.closest('product-form');
    const productIdAttr =
      form.getAttribute('data-product-id') ||
      (productFormEl && productFormEl.getAttribute('data-product-id'));
    const bodyHandle = document.body.getAttribute('data-product-handle');
    const data = window.__miniCartUpsellProduct;

    // Only the actual main product form — not every /cart/add form on the PDP.
    if (data && productIdAttr && String(productIdAttr) === String(data.id)) {
      return true;
    }

    return !!(bodyHandle && form.closest('.product__info'));
  }

  function isUpsellEligibleForm(form) {
    if (!isMainPdpForm(form)) return false;
    const data = window.__miniCartUpsellProduct || {};
    return data.upsellOnAtc === true &&
      Array.isArray(data.upsellProducts) &&
      data.upsellProducts.some((p) => p && p.available !== false);
  }

  function setPdpAtcLoading(form, isLoading) {
    if (!form) return;
    form.querySelectorAll('button[name="add"]').forEach((btn) => {
      if (isLoading) {
        btn.classList.add('loading');
        btn.disabled = true;
      } else {
        btn.classList.remove('loading');
        btn.disabled = false;
      }
    });
  }

  function isJazzupAddonSelected() {
    return getSelectedJazzupAddonContainers().length > 0;
  }

  async function handleMainPdpAdd(form) {
    const variantId = resolveVariantId(form);
    if (!variantId) return;

    setPdpAtcLoading(form, true);
    try {
      await addItems(buildAtcLineItems(variantId, getFormQuantity(form)));
    } catch (err) {
      console.error('[MiniCartUpsell] PDP add failed', err);
    } finally {
      setPdpAtcLoading(form, false);
    }
  }

  /**
   * People also bought ATC popup disabled — Candy Rack owns the upsell popup.
   * We pre-add the main PDP item as soon as CR intercepts ATC so closing the
   * popup can open the theme cart immediately (CR otherwise adds on Continue).
   *
   * CR often re-adds ONLY the main variant on CONTINUE (not the full pre-add
   * set). Exact size-match short-circuits miss that and double qty — especially
   * when a Complete Your Table addon was pre-added with the main line.
   * Keep blocked ids briefly after close; swallow fetch + XHR duplicates.
   */
  let candyRackPreAdd = null;
  let candyRackBlockIds = null;
  let candyRackBlockClearTimer = null;

  function getFormFromCandyButton(button) {
    if (!button || !button.closest) return null;
    return (
      button.closest('form[data-type="add-to-cart-form"]') ||
      button.closest('form[action*="/cart/add"]')
    );
  }

  function findMainPdpForm() {
    const forms = document.querySelectorAll(
      'form[data-type="add-to-cart-form"], form[action*="/cart/add"]'
    );
    for (let i = 0; i < forms.length; i++) {
      if (isMainPdpForm(forms[i])) return forms[i];
    }
    return null;
  }

  function setCandyRackBlockIds(ids) {
    if (candyRackBlockClearTimer) {
      window.clearTimeout(candyRackBlockClearTimer);
      candyRackBlockClearTimer = null;
    }
    candyRackBlockIds = ids && ids.size ? new Set(ids) : null;
  }

  function scheduleCandyRackBlockClear(ms) {
    if (candyRackBlockClearTimer) window.clearTimeout(candyRackBlockClearTimer);
    candyRackBlockClearTimer = window.setTimeout(() => {
      candyRackBlockIds = null;
      candyRackBlockClearTimer = null;
    }, ms);
  }

  function extractCartAddVariantIds(raw) {
    const found = [];
    const pushId = (v) => {
      if (v == null || v === '') return;
      found.push(String(v));
    };
    if (raw == null) return found;
    let body = null;
    if (typeof raw === 'string') {
      try {
        body = JSON.parse(raw);
      } catch (_) {
        try {
          body = {};
          new URLSearchParams(raw).forEach((value, key) => {
            body[key] = value;
          });
        } catch (_) {
          return found;
        }
      }
    } else if (typeof FormData !== 'undefined' && raw instanceof FormData) {
      body = {};
      raw.forEach((value, key) => {
        body[key] = value;
      });
    } else if (typeof raw === 'object') {
      body = raw;
    }
    if (!body) return found;
    if (Array.isArray(body.items) && body.items.length) {
      body.items.forEach((item) => {
        pushId(item && (item.id != null ? item.id : item.variant_id));
      });
    }
    pushId(body.id);
    Object.keys(body).forEach((key) => {
      if (key === 'id' || /\[id\]$/i.test(key) || /^items(\[\d*\])?\[id\]$/i.test(key)) {
        pushId(body[key]);
      }
    });
    return found;
  }

  function activeCandyRackBlockedIds() {
    const ids = new Set();
    if (candyRackBlockIds) {
      candyRackBlockIds.forEach((id) => ids.add(String(id)));
    }
    if (candyRackPreAdd && candyRackPreAdd.variantIds) {
      candyRackPreAdd.variantIds.forEach((id) => ids.add(String(id)));
    }
    return ids;
  }

  function isDuplicateCandyRackAdd(body) {
    const blocked = activeCandyRackBlockedIds();
    if (!blocked.size) return false;
    const reqIds = extractCartAddVariantIds(body);
    if (!reqIds.length) return false;
    // Subset match: CR CONTINUE often posts only the main id after we already
    // pre-added main + addon(s).
    return reqIds.every((id) => blocked.has(String(id)));
  }

  function fakeCartAddResponse(data) {
    return new Response(JSON.stringify(data || { items: [] }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function startCandyRackPreAdd(form) {
    if (!form || !isMainPdpForm(form)) return null;
    const variantId = resolveVariantId(form);
    if (!variantId) return null;

    const lineItems = buildAtcLineItems(variantId);
    const signature = lineItems.map((item) => String(item.id) + ':' + String(item.quantity)).join(',');
    // Only coalesce an in-flight pre-add. Reusing a completed promise skips
    // /cart/add after the cart was cleared (common on the default 4-person
    // variant: ATC → CR → remove/clear → ATC again → spinner, empty cart).
    if (
      candyRackPreAdd &&
      candyRackPreAdd.signature === signature &&
      candyRackPreAdd.promise &&
      !candyRackPreAdd.completed &&
      !candyRackPreAdd.failed
    ) {
      return candyRackPreAdd.promise;
    }

    const variantIds = new Set(lineItems.map((item) => String(item.id)));
    // Intentional qty from THIS pre-add only — never trust cart/add response
    // line.quantity (Shopify returns merged cart totals after a race → expected
    // becomes 2 and reconcile stops fixing 8/12/16 person doubles).
    const expectedQtyById = {};
    lineItems.forEach((item) => {
      const id = String(item.id);
      expectedQtyById[id] = (expectedQtyById[id] || 0) + (Number(item.quantity) || 1);
    });
    setCandyRackBlockIds(variantIds);
    window.__mcuCandyRackExpectedQty = expectedQtyById;

    setPdpAtcLoading(form, true);
    const promise = addItems(lineItems, { skipOpenDrawer: true })
      .then((data) => {
        if (candyRackPreAdd && candyRackPreAdd.signature === signature) {
          candyRackPreAdd.completed = true;
          candyRackPreAdd.data = data;
        }
        resetJazzupAddonSelection();
        // If anything else raced into the cart, snap back immediately.
        scheduleCandyRackQtyReconcile(expectedQtyById, 20000);
        return data;
      })
      .catch((err) => {
        console.error('[MiniCartUpsell] Candy Rack pre-add failed', err);
        if (candyRackPreAdd && candyRackPreAdd.signature === signature) {
          candyRackPreAdd.failed = true;
        }
        throw err;
      })
      .finally(() => {
        setPdpAtcLoading(form, false);
      });

    candyRackPreAdd = {
      signature,
      variantIds,
      expectedQtyById,
      promise,
      form,
      completed: false,
      failed: false,
      data: null,
    };
    return promise;
  }

  function shouldBlockDuplicateMainAdd(form) {
    if (!form || !isMainPdpForm(form)) return false;
    const blocked = activeCandyRackBlockedIds();
    if (!blocked.size) return false;
    if (candyRackPreAdd && candyRackPreAdd.failed) return false;
    const variantId = resolveVariantId(form);
    if (!variantId || !blocked.has(String(variantId))) return false;

    // Only suppress native ATC while a pre-add is in-flight or CR popup is open.
    // Do NOT block on leftover candyRackBlockIds or lingering CR "pass-through"
    // after close — that blocked re-ATC (empty cart) while CONTINUE/theme adds
    // are already covered by cart-aware short-circuit + reconcile.
    const preInFlight = !!(
      candyRackPreAdd &&
      candyRackPreAdd.promise &&
      !candyRackPreAdd.completed &&
      !candyRackPreAdd.failed
    );
    if (preInFlight) return true;
    const cr = window.CandyRack;
    if (!cr) return false;
    return !!(cr.popupOpen || cr.buttonState === 'handling');
  }

  function cartAlreadyHasVariantIds(cart, ids) {
    if (!cart || !ids || !ids.length) return false;
    const have = new Set(
      (cart.items || []).map((item) => String(item.variant_id != null ? item.variant_id : item.id))
    );
    return ids.every((id) => have.has(String(id)));
  }

  /**
   * Short-circuit CR/theme duplicate /cart/add.
   * - Live pre-add: fake with pre-add payload (prevents qty 2 on CONTINUE).
   * - Orphan block ids after close: fake only if cart still has those variants
   *   (late CR re-add). If cart was cleared, allow a real add (Tia 4pc re-ATC).
   */
  function shortCircuitDuplicateCartAdd(nativeFetch, input, init, body) {
    scheduleCandyRackBlockClear(20000);
    const pre = candyRackPreAdd;
    if (pre && pre.promise && !pre.failed) {
      return pre.promise.then((data) => {
        if (pre.failed) return nativeFetch(input, init);
        return fakeCartAddResponse(data || pre.data);
      });
    }
    const reqIds = extractCartAddVariantIds(body);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return nativeFetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => (r && r.ok ? r.json() : null))
      .then((cart) => {
        if (cartAlreadyHasVariantIds(cart, reqIds)) {
          return fakeCartAddResponse(cart || { items: [] });
        }
        // Cart no longer has the blocked variants (removed/cleared) — real add.
        return nativeFetch(input, init);
      })
      .catch(() => nativeFetch(input, init));
  }

  let candyRackReconcileTimer = null;
  let candyRackReconcileBusy = false;

  function scheduleCandyRackQtyReconcile(expectedQtyById, ms) {
    if (!expectedQtyById || !Object.keys(expectedQtyById).length) return;
    window.__mcuCandyRackExpectedQty = expectedQtyById;
    if (candyRackReconcileTimer) {
      window.clearInterval(candyRackReconcileTimer);
      candyRackReconcileTimer = null;
    }
    const reconcile = () => {
      if (candyRackReconcileBusy) return;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js')
        .then((r) => r.json())
        .then((cart) => {
          const over = [];
          (cart.items || []).forEach((item) => {
            const id = String(item.variant_id);
            const want = expectedQtyById[id];
            if (!want || item.quantity <= want) return;
            over.push({ key: item.key, want });
          });
          if (!over.length) return;
          candyRackReconcileBusy = true;
          Promise.all(
            over.map((row) =>
              fetch(root + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                  id: row.key,
                  quantity: row.want,
                  sections: getSectionsRequestParam(),
                  sections_url: window.location.pathname,
                }),
              }).then((r) => (r.ok ? r.json() : null))
            )
          )
            .then((payloads) => {
              candyRackReconcileBusy = false;
              const last = payloads.filter(Boolean).pop();
              if (last) return refreshCartDrawerUI(last);
              return fetchAndRefreshCartDrawer();
            })
            .catch(() => {
              candyRackReconcileBusy = false;
            });
        })
        .catch(() => {});
    };
    reconcile();
    const started = Date.now();
    candyRackReconcileTimer = window.setInterval(() => {
      if (Date.now() - started > (ms || 20000)) {
        window.clearInterval(candyRackReconcileTimer);
        candyRackReconcileTimer = null;
        return;
      }
      reconcile();
    }, 700);
  }

  function patchCandyRackFrameWindow(win) {
    if (!win || win === window || win.__mcuCandyRackFramePatched) return;
    try {
      win.__mcuCandyRackFramePatched = true;
      const frameFetch = win.fetch && win.fetch.bind(win);
      if (frameFetch) {
        win.fetch = function (input, init) {
          const url =
            typeof input === 'string'
              ? input
              : input && typeof input.url === 'string'
                ? input.url
                : '';
          const path = url.split('?')[0];
          if (/\/cart\/add(\.js)?$/.test(path) && isDuplicateCandyRackAdd(init && init.body)) {
            scheduleCandyRackBlockClear(20000);
            const expected = window.__mcuCandyRackExpectedQty;
            if (expected) scheduleCandyRackQtyReconcile(expected, 20000);
            const pre = candyRackPreAdd;
            if (pre && pre.promise && !pre.failed) {
              return pre.promise.then((data) => fakeCartAddResponse(data || pre.data || { items: [] }));
            }
            return Promise.resolve(fakeCartAddResponse({ items: [] }));
          }
          return frameFetch(input, init);
        };
      }
      const frameXhrProto = win.XMLHttpRequest && win.XMLHttpRequest.prototype;
      if (frameXhrProto && !frameXhrProto.__mcuOpenPatched) {
        frameXhrProto.__mcuOpenPatched = true;
        const frameOpen = frameXhrProto.open;
        const frameSend = frameXhrProto.send;
        frameXhrProto.open = function (method, url) {
          this.__mcuMethod = method;
          this.__mcuUrl = url;
          return frameOpen.apply(this, arguments);
        };
        frameXhrProto.send = function (body) {
          const url = String(this.__mcuUrl || '');
          const path = url.split('?')[0];
          const method = String(this.__mcuMethod || 'GET').toUpperCase();
          if (method === 'POST' && /\/cart\/add(\.js)?$/.test(path) && isDuplicateCandyRackAdd(body)) {
            scheduleCandyRackBlockClear(20000);
            const expected = window.__mcuCandyRackExpectedQty;
            if (expected) scheduleCandyRackQtyReconcile(expected, 20000);
            const text = JSON.stringify({ items: [] });
            try {
              Object.defineProperty(this, 'status', { configurable: true, value: 200 });
              Object.defineProperty(this, 'readyState', { configurable: true, value: 4 });
              Object.defineProperty(this, 'responseText', { configurable: true, value: text });
              Object.defineProperty(this, 'response', { configurable: true, value: text });
            } catch (_) {
              this.status = 200;
              this.responseText = text;
            }
            if (typeof this.onload === 'function') this.onload();
            if (typeof this.onreadystatechange === 'function') this.onreadystatechange();
            return;
          }
          return frameSend.apply(this, arguments);
        };
      }
    } catch (_) {}
  }

  function watchCandyRackFrame() {
    if (window.__mcuCandyRackFrameWatch) return;
    window.__mcuCandyRackFrameWatch = true;
    const tryPatch = () => {
      const frame = document.getElementById('candyrack-frame');
      if (!frame) return;
      try {
        patchCandyRackFrameWindow(frame.contentWindow);
      } catch (_) {}
    };
    tryPatch();
    window.setInterval(tryPatch, 500);
  }

  async function onCandyRackClosed() {
    let data = candyRackPreAdd && candyRackPreAdd.data;
    try {
      if (candyRackPreAdd && candyRackPreAdd.promise) {
        data = await candyRackPreAdd.promise;
      }
    } catch (_) {}

    try {
      if (data && data.sections) {
        await refreshCartDrawerUI(data);
      } else {
        await fetchAndRefreshCartDrawer();
      }
    } catch (err) {
      console.error('[MiniCartUpsell] cart refresh after Candy Rack failed', err);
    }

    openCartDrawer();
    if (typeof window.JazzOpenCartDrawer === 'function') {
      try {
        window.JazzOpenCartDrawer();
      } catch (_) {}
    }
    dispatchRefresh({ openDrawer: true });

    // Always reconcile to INTENTIONAL pre-add qtys (not Shopify merged response qty).
    const expectedQtyById =
      (candyRackPreAdd && candyRackPreAdd.expectedQtyById) ||
      window.__mcuCandyRackExpectedQty ||
      {};
    if (!Object.keys(expectedQtyById).length && candyRackPreAdd && candyRackPreAdd.variantIds) {
      candyRackPreAdd.variantIds.forEach((id) => {
        expectedQtyById[String(id)] = 1;
      });
    }
    window.setTimeout(() => {
      candyRackPreAdd = null;
    }, 1500);
    // Keep block ids ~20s so late CR /cart/add is short-circuited (Ava/Tia qty 2).
    // Empty-cart fakes after clear are avoided by cart-aware shortCircuit below.
    scheduleCandyRackBlockClear(20000);
    scheduleCandyRackQtyReconcile(expectedQtyById, 20000);
  }

  function wireCandyRackBridge() {
    if (window.__mcuCandyRackWired) return;
    window.__mcuCandyRackWired = true;
    watchCandyRackFrame();

    document.addEventListener('addToCartButtonClicked', (event) => {
      const detail = event && event.detail;
      const button = detail && (detail.hiddenButton || detail.clonedButton);
      const formFromButton = getFormFromCandyButton(button);

      // Candy Rack intercepts quick-view ATC clicks. Do NOT add here —
      // hand off to the modal so quantity is applied once (avoids double-add).
      if (
        (button && button.closest && button.closest('.quick-add-modal, [data-quick-add-modal]')) ||
        isQuickAddForm(formFromButton)
      ) {
        const form = formFromButton || (button && button.closest && button.closest('form'));
        if (!form) return;
        form.dispatchEvent(new CustomEvent('qa:force-add', { bubbles: false, cancelable: true }));
        return;
      }

      const form = formFromButton || findMainPdpForm();
      if (!form) return;
      startCandyRackPreAdd(form);
    });

    document.addEventListener('candyrack-closed', () => {
      void onCandyRackClosed();
    });

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest(
          'button[name="add"], input[name="add"], button[type="submit"][name="add"]'
        );
        if (!button || button.classList.contains('candy-cloned')) return;
        const form = getFormFromCandyButton(button);
        if (!shouldBlockDuplicateMainAdd(form)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest(
          'form[data-type="add-to-cart-form"], form[action*="/cart/add"]'
        );
        if (!shouldBlockDuplicateMainAdd(form)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    if (!window.__mcuCandyRackFetchPatched) {
      window.__mcuCandyRackFetchPatched = true;
      const nativeFetch = window.fetch.bind(window);
      window.fetch = function (input, init) {
        const url =
          typeof input === 'string' ? input : input && typeof input.url === 'string' ? input.url : '';
        const path = url.split('?')[0];
        // Never short-circuit our own pre-add / addItems fetch.
        if (mcuOwnPreAddDepth > 0) {
          return nativeFetch(input, init);
        }
        if (/\/cart\/add(\.js)?$/.test(path) && isDuplicateCandyRackAdd(init && init.body)) {
          return shortCircuitDuplicateCartAdd(nativeFetch, input, init, init && init.body);
        }
        return nativeFetch(input, init);
      };
    }

    if (!window.__mcuCandyRackXhrPatched) {
      window.__mcuCandyRackXhrPatched = true;
      const xhrProto = XMLHttpRequest.prototype;
      const nativeXhrOpen = xhrProto.open;
      const nativeXhrSend = xhrProto.send;
      xhrProto.open = function (method, url) {
        this.__mcuMethod = method;
        this.__mcuUrl = url;
        return nativeXhrOpen.apply(this, arguments);
      };
      xhrProto.send = function (body) {
        const xhr = this;
        if (xhr.__avaOwnPreAdd || xhr.__avaBypassCandyShortCircuit || xhr.__mcuOwnPreAdd) {
          return nativeXhrSend.apply(xhr, arguments);
        }
        const url = String(xhr.__mcuUrl || '');
        const path = url.split('?')[0];
        const method = String(xhr.__mcuMethod || 'GET').toUpperCase();
        if (method === 'POST' && /\/cart\/add(\.js)?$/.test(path) && isDuplicateCandyRackAdd(body)) {
          scheduleCandyRackBlockClear(20000);
          const finishFake = (data) => {
            const text = JSON.stringify(data || { items: [] });
            try {
              Object.defineProperty(xhr, 'status', { configurable: true, value: 200 });
              Object.defineProperty(xhr, 'readyState', { configurable: true, value: 4 });
              Object.defineProperty(xhr, 'responseText', { configurable: true, value: text });
              Object.defineProperty(xhr, 'response', { configurable: true, value: text });
            } catch (_) {
              xhr.status = 200;
              xhr.responseText = text;
            }
            if (typeof xhr.onload === 'function') xhr.onload();
            if (typeof xhr.onreadystatechange === 'function') xhr.onreadystatechange();
          };
          const pre = candyRackPreAdd;
          if (pre && pre.promise && !pre.failed) {
            pre.promise
              .then((data) => {
                if (pre.failed) {
                  nativeXhrSend.call(xhr, body);
                  return;
                }
                finishFake(data || pre.data);
              })
              .catch(() => {
                nativeXhrSend.call(xhr, body);
              });
            return;
          }
          // Orphan block ids: fake only if cart still has the variants.
          const reqIds = extractCartAddVariantIds(body);
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const probe = new XMLHttpRequest();
          probe.__mcuOwnPreAdd = true;
          probe.open('GET', root + 'cart.js', true);
          probe.setRequestHeader('Accept', 'application/json');
          probe.onload = function () {
            let cart = null;
            try {
              cart = JSON.parse(probe.responseText || '{}');
            } catch (_) {}
            if (cartAlreadyHasVariantIds(cart, reqIds)) {
              finishFake(cart || { items: [] });
              return;
            }
            nativeXhrSend.call(xhr, body);
          };
          probe.onerror = function () {
            nativeXhrSend.call(xhr, body);
          };
          probe.send();
          return;
        }
        return nativeXhrSend.apply(xhr, arguments);
      };
    }
  }

  async function onAtcClick(event) {
    const button = event.target.closest('button[name="add"]');
    if (!button) return;
    const form = button.closest('form[data-type="add-to-cart-form"]');
    if (!form || !isMainPdpForm(form)) return;

    const eligible = isUpsellEligibleForm(form);

    if (eligible) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const variantId = resolveVariantId(form);
      if (!variantId) return;
      const data = window.__miniCartUpsellProduct || {};
      const lineItems = buildAtcLineItems(variantId, getFormQuantity(form));
      pendingMain = { variantId, title: data.title || '' };
      shouldOpenCartOnClose = true;

      setPdpAtcLoading(form, true);

      try {
        await addItems(lineItems, { skipOpenDrawer: true });
        resetJazzupAddonSelection();
      } catch (err) {
        console.error('[MiniCartUpsell] main product add failed', err);
        setPdpAtcLoading(form, false);
        pendingMain = null;
        shouldOpenCartOnClose = false;
        return;
      }

      setPdpAtcLoading(form, false);
      openPopup();
      return;
    }

    if (isJazzupAddonSelected()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    await handleMainPdpAdd(form);
  }

  function onAtcSubmit(event) {
    const form = event.target.closest('form[data-type="add-to-cart-form"]');
    if (!form || !isMainPdpForm(form)) return;

    if (isUpsellEligibleForm(form) || !isJazzupAddonSelected()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  function onCartRefreshOpenDrawer(event) {
    const detail = event && event.detail;
    syncHeaderCartCounts(null, { data: detail });
    if (detail && detail.openDrawer) {
      closePresellPopupIfOpen();
      openCartDrawer();
      if (detail.sections) {
        applyDrawerFromSectionsPayload(detail.sections);
        syncUpsellFromSections(detail.sections);
        syncDrawerEmptyState();
      }
    }
  }

  function applyCartDrawerUpsellScript(mountEl) {
    if (!mountEl) return;
    const script = mountEl.querySelector('script');
    if (!script || !script.textContent) return;
    try {
      // eslint-disable-next-line no-eval
      eval(script.textContent);
    } catch (err) {
      console.error('[MiniCartUpsell] cart drawer upsell script failed', err);
    }
  }

  function updateUpsellMountVisibility(mountEl) {
    if (!mountEl) return;
    const slides = mountEl.querySelectorAll('[data-cart-drawer-upsell-slide]');
    if (!slides.length) {
      if (!mountEl.hasAttribute('data-cart-drawer-upsell-empty')) {
        mountEl.hidden = true;
        mountEl.setAttribute('data-cart-drawer-upsell-empty', '');
      }
      return;
    }
    const hasVisibleSlide = Array.from(slides).some((slide) => !slide.hidden);
    mountEl.hidden = !hasVisibleSlide;
    if (hasVisibleSlide) {
      mountEl.removeAttribute('data-cart-drawer-upsell-empty');
    } else {
      mountEl.setAttribute('data-cart-drawer-upsell-empty', '');
    }
  }

  async function pruneUpsellSlidesForCart(mountEl) {
    if (!mountEl) return;
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!res.ok) return;
      const cart = await res.json();
      const inCart = new Set((cart.items || []).map((item) => String(item.product_id)));
      mountEl.querySelectorAll('[data-cart-drawer-upsell-slide]').forEach((slide) => {
        const productId = slide.getAttribute('data-product-id');
        slide.hidden = !!(productId && inCart.has(String(productId)));
      });
      updateUpsellMountVisibility(mountEl);
    } catch (_) {}
  }

  function getCartDrawerBlocksEl(drawer) {
    if (!drawer) return null;
    return (
      drawer.querySelector('.cart-drawer__blocks.cart-drawer__element-for-non-empty-cart') ||
      drawer.querySelector('.cart-drawer__blocks')
    );
  }

  function upsellMountNeedsRefresh(mountEl) {
    if (!mountEl) return true;
    const slides = mountEl.querySelectorAll('[data-cart-drawer-upsell-slide]');
    if (!slides.length) return true;
    return !Array.from(slides).some((slide) => !slide.hidden);
  }

  function syncCartDrawerUpsell(sectionNode) {
    if (!sectionNode) return;
    const drawer = getCartDrawer();
    if (!drawer) return;

    const blocks = getCartDrawerBlocksEl(drawer);
    if (!blocks) return;

    const hasItems = !!drawer.querySelector('cart-item');
    const nextMount = findUpsellMountInSectionRoot(sectionNode);
    const currentMount = drawer.querySelector('[data-cart-drawer-upsell-mount]');

    if (nextMount) {
      const clone = nextMount.cloneNode(true);
      applyCartDrawerUpsellScript(clone);
      updateUpsellMountVisibility(clone);
      if (currentMount) {
        currentMount.replaceWith(clone);
      } else {
        const itemsBlock = blocks.querySelector('cart-drawer-items-block');
        if (itemsBlock && itemsBlock.nextSibling) {
          itemsBlock.parentNode.insertBefore(clone, itemsBlock.nextSibling);
        } else {
          blocks.appendChild(clone);
        }
      }
    } else if (currentMount && !hasItems) {
      currentMount.remove();
      window.__cartDrawerUpsellProducts = [];
    } else if (currentMount && hasItems) {
      void pruneUpsellSlidesForCart(currentMount);
      if (upsellMountNeedsRefresh(currentMount)) {
        void ensureCartDrawerUpsellMounted(true);
      }
    } else if (hasItems) {
      void ensureCartDrawerUpsellMounted(true);
    }
  }

  async function ensureCartDrawerUpsellMounted(force) {
    const drawer = getCartDrawer();
    if (!drawer || !drawer.querySelector('cart-item')) return;
    const currentMount = drawer.querySelector('[data-cart-drawer-upsell-mount]');
    if (currentMount && !force && !upsellMountNeedsRefresh(currentMount)) return;

    const payload = await fetchSectionHtmlPayload();
    if (payload && payload.sections) {
      syncUpsellFromSections(payload.sections);
    }
  }

  function wireCartDrawerCountSync() {
    const drawer = getCartDrawer();
    if (!drawer || drawer.dataset.mcuCountWired === 'true') return;
    drawer.dataset.mcuCountWired = 'true';
    if (typeof drawer.subscribe === 'function') {
      drawer.subscribe('start-update', () => {
        beginCartTotalsUpdate();
      });
      drawer.subscribe('update-nodes', ({ node }) => {
        if (node) {
          const countEl = findNewCartCountEl(node);
          if (countEl) {
            applyCartCountFromNode(countEl);
          } else {
            syncHeaderCartCounts(node);
          }
          syncCartDrawerUpsell(node);
          syncDrawerEmptyState();
          setTimeout(() => {
            syncDrawerEmptyState();
            ensureCartDrawerUpsellMounted(true);
          }, 0);
          scheduleCartTotalsFinish(80);
        } else {
          // update-nodes with no node still needs to unblock the drawer
          scheduleCartTotalsFinish(80);
        }
      });
    }
    applyCartDrawerUpsellScript(drawer.querySelector('[data-cart-drawer-upsell-mount]'));
  }

  function wireGlobalCartCountSync() {
    if (window.__mcuCartFetchWired) return;
    window.__mcuCartFetchWired = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const url =
        typeof input === 'string'
          ? input
          : input && typeof input.url === 'string'
            ? input.url
            : '';
      const path = url.split('?')[0];
      const isCartMutation = /\/cart\/(add|change|update)(\.js)?$/.test(path);
      return nativeFetch(input, init).then((res) => {
        if (isCartMutation && res && res.ok) {
          res
            .clone()
            .json()
            .then((data) => {
              syncHeaderCartCounts(null, { data: data || null });
              if (!(data && data.sections)) {
                beginCartTotalsUpdate();
                scheduleCartTotalsFinish(300);
              }
            })
            .catch(() => {
              scheduleCartCountVerify(0);
              scheduleCartTotalsFinish(300);
            });
        }
        return res;
      });
    };
  }

  function wireAtcListeners() {
    wireGlobalCartCountSync();
    wireCandyRackBridge();
    document.documentElement.addEventListener('cart:refresh', onCartRefreshOpenDrawer);
    window.addEventListener('cart:refresh', onCartRefreshOpenDrawer);
    let cartUiRefreshTimer = null;
    window.addEventListener('cart:updated', (event) => {
      scheduleSavingsRefresh(250);
      const detail = event && event.detail;
      syncHeaderCartCounts(null, { data: detail });
      if (detail && detail.__mcuDrawerSynced) return;
      clearTimeout(cartUiRefreshTimer);
      cartUiRefreshTimer = setTimeout(() => {
        if (detail && detail.sections) {
          applyDrawerFromSectionsPayload(detail.sections);
          syncUpsellFromSections(detail.sections);
          syncHeaderCartCounts(null, { data: detail });
          syncDrawerEmptyState();
          ensureCartDrawerUpsellMounted(true);
        } else if (isCartDrawerMounted() && isCartDrawerOpen()) {
          void fetchAndRefreshCartDrawer();
        }
        scheduleSavingsRefresh(200);
      }, 60);
    });
    document.addEventListener(
      'click',
      function (event) {
        void onAtcClick(event);
      },
      true
    );
    document.addEventListener('submit', onAtcSubmit, true);
  }

  function init() {
    wirePopupControls();
    wireProductDrawerControls();
    wireCartDrawerCountSync();
    syncHeaderCartCounts();
    document.addEventListener(
      'click',
      (event) => {
        if (
          event.target.closest(
            '[data-sidebar-id="CartDrawer"], [data-sidebar-button][data-sidebar-id="CartDrawer"]'
          )
        ) {
          setTimeout(() => {
            syncDrawerEmptyState();
            ensureCartDrawerUpsellMounted(true);
          }, 0);
        }
      },
      true
    );
    document.addEventListener(
      'click',
      (event) => {
        const removeButton = event.target.closest(
          '#CartDrawer cart-remove-button, #CartDrawer .cart-item__remove-button'
        );
        if (!removeButton) return;

        const cartItem = removeButton.closest('cart-item');
        const variantId = cartItem && cartItem.getAttribute('data-variant-id');
        const removeKey = getCartItemRemoveKey(cartItem);
        if (!variantId) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (cartItem && cartItem.classList.contains('is-removing')) {
          return;
        }
        if (removeKey && removingCartItemKeys.has(removeKey)) {
          return;
        }
        enqueueRemoveAction(variantId, cartItem);
      },
      true
    );
    document.addEventListener('click', (event) => {
      const cartUpsellBtn = event.target.closest('[data-cart-drawer-upsell-open]');
      if (cartUpsellBtn) {
        event.preventDefault();
        event.stopPropagation();
        const product = findCartDrawerUpsellProduct(cartUpsellBtn.getAttribute('data-product-id'));
        if (!product) return;
        openProductDrawer(product);
        return;
      }

      if (event.target.closest('#CartDrawer quantity-btn, #CartDrawer [data-quantity-input]')) {
        // Don't begin here — cart-drawer `start-update` already does.
        // Keep a safety finish in case update-nodes is delayed or skipped (e.g. API error).
        scheduleCartTotalsFinish(900);
        scheduleCartCountVerify(400);
      }

      if (
        event.target.closest('cart-remove-button') ||
        event.target.closest('.cart-item__remove-button')
      ) {
        scheduleCartCountVerify(400);
      }
    });
    document.addEventListener('change', (event) => {
      if (
        event.target.matches('[data-quantity-input], .cart-item-quantity__input') &&
        event.target.closest('#CartDrawer')
      ) {
        scheduleCartTotalsFinish(900);
      }
    });
    window.addEventListener('cart:refresh', () => scheduleSavingsRefresh(350));
    scheduleSavingsRefresh(50);
  }

  window.MiniCartUpsellPDP = {
    addItems,
    refreshCartAfterAdd: refreshCartDrawerUI,
    fetchAndRefreshCartDrawer,
    refreshAndOpenDrawer,
    syncHeaderCartCounts,
    applyHeaderCartCount,
    setPdpAtcLoading,
    scheduleSavingsRefresh,
    refreshCartTotalsDisplay,
  };

  wireAtcListeners();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
